import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL, db } from "../../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import {
  FaBoxes,
  FaCopy,
  FaEdit,
  FaFilePdf,
  FaMinus,
  FaPaperclip,
  FaPlus,
  FaSave,
  FaShareAlt,
  FaTrash,
} from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import { FEATURES, hasPermission, ROLES } from "../../lib/rolesConfig";

const emptyInventoryForm = {
  profileId: "",
  medicationId: "",
  medicationName: "",
  genericName: "",
  strength: "",
  form: "",
  unitLabel: "unit",
  quantityOnHand: "",
  deductQuantityPerAdministration: "1",
  lowStockThreshold: "7",
  notes: "",
};

const emptyOrderDraft = {
  profileId: "",
  inventoryId: "",
  customMedicationName: "",
  customStrength: "",
  customForm: "",
  quantity: "1",
  unitLabel: "unit",
  notes: "",
};

const emptyAdjustment = {
  mode: "set",
  quantity: "",
  reason: "",
};

const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getDateValue = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (timestamp) => {
  const date = getDateValue(timestamp);
  if (!date) return "Not recorded";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const formatDateOnly = (timestamp) => {
  const date = getDateValue(timestamp);
  if (!date) return "Not recorded";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
};

const sanitizeForFirestore = (value) => {
  if (value === undefined) return null;
  if (value === null) return null;
  if (Array.isArray(value)) return value.map(sanitizeForFirestore);
  if (value instanceof Date || typeof value?.toDate === "function")
    return value;
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        sanitizeForFirestore(nestedValue),
      ]),
    );
  }
  return value;
};

const getProfileLabel = (profile) => profile?.profileName || "Unnamed patient";

const getSeniorId = (profile, seniorIds = {}) =>
  seniorIds[profile?.id] ||
  profile?.seniorId ||
  profile?.seniorID ||
  profile?.seniorCitizenId ||
  "";

const getProfileTransactionType = (profileId, transactionTypesByProfile = {}) =>
  transactionTypesByProfile[profileId] || "normal";

const getTransactionGroupKey = (transactionType) =>
  transactionType === "senior" ? "senior" : "normal";

const getTransactionGroupLabel = (transactionType) =>
  getTransactionGroupKey(transactionType) === "senior"
    ? "Senior Citizen Transaction"
    : "Regular Transaction";

const getStockStatus = (item) => {
  if (!item.isInventoryTracked) {
    return {
      label: "Not monitored",
      classes: "border border-slate-200 bg-white text-slate-500",
    };
  }
  const quantity = toNumber(item.quantityOnHand);
  const threshold = toNumber(item.lowStockThreshold);
  if (quantity <= 0) {
    return { label: "Out of stock", classes: "bg-red-100 text-red-700" };
  }
  if (threshold > 0 && quantity <= threshold) {
    return { label: "Low stock", classes: "bg-amber-100 text-amber-700" };
  }
  return { label: "In stock", classes: "bg-green-100 text-green-700" };
};

const getStockStatusDotClasses = (label) => {
  if (label === "Out of stock") return "bg-red-500";
  if (label === "Low stock") return "bg-amber-500";
  if (label === "In stock") return "bg-green-500";
  return "bg-slate-300";
};

const getThresholdSuggestedOrderQuantity = (item) => {
  if (!item.isInventoryTracked) return 0;
  const quantity = toNumber(item.quantityOnHand);
  const threshold = toNumber(item.lowStockThreshold);
  if (threshold <= 0 || quantity > threshold) return 0;
  return Math.max(1, Math.ceil(threshold * 3 - quantity));
};

const getGenericName = (item) => String(item?.genericName || "").trim();

const getOrderSelectionGenericName = (medicineRow, orderItem) =>
  getGenericName(orderItem) || getGenericName(medicineRow);

const getOrderMedicationName = (item) => {
  const genericName = getGenericName(item);
  return item?.useGenericAlternative && genericName
    ? genericName
    : item?.medicationName || "Medicine";
};

const getMedicationOrderPdfFilename = (order) => {
  const profiles = order.profileSummaries || [];
  const orderDate = getDateValue(order.finalizedAt) || new Date();
  const yyyy = String(orderDate.getFullYear());
  const mm = String(orderDate.getMonth() + 1).padStart(2, "0");
  const dd = String(orderDate.getDate()).padStart(2, "0");
  const hh = String(orderDate.getHours()).padStart(2, "0");
  const min = String(orderDate.getMinutes()).padStart(2, "0");
  const patientName =
    profiles.length === 1
      ? profiles[0]?.patientName || "Patient"
      : "Consolidated Group";
  const safePatientName = patientName
    .replace(/[<>:"/\\|?*]+/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return `MILA CARE PH Meds Order ${yyyy}${mm}${dd} ${hh}-${min} - ${
    safePatientName || "Patient"
  }.pdf`;
};

const formatMedicationLine = (item) => {
  const details = [item.strength, item.form].filter(Boolean).join(" ");
  const notes = item.notes ? ` - ${item.notes}` : "";
  const name = `${getOrderMedicationName(item)}${
    item.isNewMedication ? "*" : ""
  }`;
  return `${name}${details ? ` (${details})` : ""}: ${toNumber(item.quantity)} ${
    item.unitLabel || "unit"
  }${notes}`;
};

const getPrescriptionName = (prescription) =>
  prescription?.prescriptionName ||
  prescription?.medicationName ||
  "Prescription";

const getPrescriptionDate = (prescription) =>
  prescription?.prescriptionDate || prescription?.prescribedDate;

const getPrescriptionFileExtension = (prescription) =>
  String(prescription?.fileName || "")
    .split(".")
    .pop()
    .toLowerCase();

const isImagePrescription = (prescription) =>
  ["jpg", "jpeg", "png"].includes(getPrescriptionFileExtension(prescription));

const isPdfPrescription = (prescription) =>
  getPrescriptionFileExtension(prescription) === "pdf";

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const drawPrescriptionPlaceholder = async (pdfDoc, prescription, message) => {
  const page = pdfDoc.addPage();
  const { width, height } = page.getSize();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  page.drawText("Attached prescription", {
    x: 40,
    y: height - 60,
    size: 16,
    font: boldFont,
    color: rgb(0.05, 0.35, 0.5),
  });
  page.drawText(
    `${prescription.patientName || "Patient"} - ${
      prescription.prescriptionName || "Prescription"
    }`,
    { x: 40, y: height - 88, size: 12, font },
  );
  page.drawText(message, {
    x: 40,
    y: height - 116,
    size: 10,
    font,
    color: rgb(0.35, 0.35, 0.35),
    maxWidth: width - 80,
  });
};

const buildMedicationOrderTextNotes = (order) => {
  const profiles = order.profileSummaries || [];
  const items = order.items || [];
  const profileById = profiles.reduce((acc, profile) => {
    acc[profile.careProfileId] = profile;
    return acc;
  }, {});
  const groups = {
    normal: [],
    senior: [],
  };

  items.forEach((item) => {
    const profile = profileById[item.careProfileId] || {};
    const groupKey = getTransactionGroupKey(
      item.transactionType || profile.transactionType,
    );
    groups[groupKey].push({ ...item, profile });
  });

  const lines = [
    "MILA CARE PH MEDS ORDER",
    `Order date: ${formatDateTime(order.finalizedAt)}`,
  ];
  const patientNames = profiles
    .map((profile) => profile.patientName)
    .filter(Boolean)
    .join(", ");
  if (patientNames) {
    lines.push(`Patient(s): ${patientNames}`);
  }
  if (order.deliveryAddress) {
    lines.push(`Delivery address: ${order.deliveryAddress}`);
  }
  if (order.deliveryInstructions) {
    lines.push(`Delivery instructions: ${order.deliveryInstructions}`);
  }
  if ((order.attachedPrescriptions || []).length > 0) {
    lines.push("", "ATTACHED PRESCRIPTIONS");
    order.attachedPrescriptions.forEach((prescription) => {
      const physician = prescription.physicianName
        ? ` | Dr: ${prescription.physicianName}`
        : "";
      const fileName = prescription.fileName
        ? ` | File: ${prescription.fileName}`
        : "";
      lines.push(
        `- ${prescription.patientName || "Patient"}: ${
          prescription.prescriptionName
        } (${formatDateOnly(prescription.prescriptionDate)})${physician}${fileName}`,
      );
    });
  }

  [
    { key: "normal", title: "REGULAR TRANSACTION" },
    { key: "senior", title: "SENIOR CITIZEN TRANSACTION" },
  ].forEach(({ key, title }) => {
    lines.push("", title);
    if (groups[key].length === 0) {
      lines.push("No medicines listed.");
      return;
    }

    const itemsByProfile = groups[key].reduce((acc, item) => {
      const profileId = item.careProfileId || "unknown";
      if (!acc[profileId]) acc[profileId] = [];
      acc[profileId].push(item);
      return acc;
    }, {});

    Object.entries(itemsByProfile).forEach(([profileId, profileItems]) => {
      const profile = profileById[profileId] || profileItems[0]?.profile || {};
      const seniorId =
        key === "senior" && profile.seniorId
          ? ` | Senior ID: ${profile.seniorId}`
          : "";
      lines.push(`${profile.patientName || "Patient"}${seniorId}`);
      profileItems.forEach((item) => {
        lines.push(`- ${formatMedicationLine(item)}`);
      });
    });
  });

  return lines.join("\n");
};

const createMedicationOrderPdfDoc = (order, logoBase64 = null) => {
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF();
  const profiles = order.profileSummaries || [];
  const items = order.items || [];
  const title =
    order.orderMode === "group"
      ? "Consolidated Medication Order"
      : "Medication Order";

  if (logoBase64) {
    pdf.addImage(logoBase64, "PNG", 14, 12, 20, 20);
  }
  const headerX = logoBase64 ? 40 : 14;
  pdf.setFontSize(10);
  pdf.setTextColor(14, 130, 192);
  pdf.text("MILA Care", headerX, 16);
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(18);
  pdf.text(title, headerX, 24);
  pdf.setFontSize(10);
  pdf.text(
    order.orderMode === "group"
      ? "Transactions: Grouped by discount category"
      : `Transaction: ${getTransactionGroupLabel(order.transactionType)}`,
    headerX,
    31,
  );
  pdf.text(`Order date: ${formatDateTime(order.finalizedAt)}`, headerX, 37);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const patientNames = profiles
    .map((profile) => profile.patientName)
    .filter(Boolean)
    .join("; ");
  const patientLines = pdf.splitTextToSize(
    `Patient(s): ${patientNames || "Not recorded"}`,
    pageWidth - 28,
  );
  pdf.text(patientLines, 14, 45);

  let nextY = 45 + patientLines.length * 5 + 4;
  if (order.deliveryAddress) {
    const addressLines = pdf.splitTextToSize(
      `Delivery address: ${order.deliveryAddress}`,
      pageWidth - 28,
    );
    pdf.text(addressLines, 14, nextY);
    nextY += addressLines.length * 5;
  }
  if (order.deliveryInstructions) {
    const instructionLines = pdf.splitTextToSize(
      `Delivery instructions: ${order.deliveryInstructions}`,
      pageWidth - 28,
    );
    pdf.text(instructionLines, 14, nextY);
    nextY += instructionLines.length * 5;
  }
  nextY += 6;
  const moveToNextPageIfNeeded = (minimumY = 260) => {
    if (nextY > minimumY) {
      pdf.addPage();
      nextY = 18;
    }
  };

  const profileTransactionTypes = profiles.reduce((acc, profile) => {
    acc[profile.careProfileId] = getTransactionGroupKey(
      profile.transactionType,
    );
    return acc;
  }, {});
  const transactionGroups = {
    normal: {},
    senior: {},
  };

  items.forEach((item) => {
    const groupKey = getTransactionGroupKey(
      item.transactionType || profileTransactionTypes[item.careProfileId],
    );
    const orderMedicationName = getOrderMedicationName(item);
    const totalKey = [
      orderMedicationName,
      item.strength || "",
      item.form || "",
      item.unitLabel || "unit",
      item.isNewMedication ? "new" : "existing",
    ].join("|");
    if (!transactionGroups[groupKey][totalKey]) {
      transactionGroups[groupKey][totalKey] = {
        medicationName: orderMedicationName,
        isNewMedication: !!item.isNewMedication,
        strength: item.strength || "",
        form: item.form || "",
        unitLabel: item.unitLabel || "unit",
        quantity: 0,
        patients: new Set(),
      };
    }
    transactionGroups[groupKey][totalKey].quantity += toNumber(item.quantity);
    if (item.patientName) {
      transactionGroups[groupKey][totalKey].patients.add(item.patientName);
    }
  });

  [
    { key: "normal", title: "Regular Transaction" },
    { key: "senior", title: "Senior Citizen Transaction" },
  ].forEach(({ key, title }) => {
    const groupTotals = Object.values(transactionGroups[key]);
    moveToNextPageIfNeeded();
    pdf.setFontSize(12);
    pdf.text(title, 14, nextY);

    if (groupTotals.length === 0) {
      pdf.setFontSize(9);
      pdf.text("No medicines in this transaction group.", 14, nextY + 6);
      nextY += 14;
      return;
    }

    autoTable(pdf, {
      startY: nextY + 4,
      head: [["Medicine", "Strength", "Form", "Total Qty", "Unit", "Patient"]],
      body: groupTotals.map((item) => [
        `${item.medicationName}${item.isNewMedication ? "*" : ""}`,
        item.strength,
        item.form,
        item.quantity,
        item.unitLabel,
        Array.from(item.patients).join(", "),
      ]),
      theme: "grid",
      headStyles: {
        fillColor: key === "senior" ? [37, 99, 235] : [22, 163, 74],
      },
      styles: { fontSize: 8, cellPadding: 2 },
    });
    nextY = pdf.lastAutoTable.finalY + 10;
  });

  const attachedPrescriptions = order.attachedPrescriptions || [];
  if (attachedPrescriptions.length > 0) {
    moveToNextPageIfNeeded();
    pdf.setFontSize(12);
    pdf.text("Attached prescriptions", 14, nextY);
    autoTable(pdf, {
      startY: nextY + 4,
      head: [["Patient", "Prescription", "Physician", "Date", "File"]],
      body: attachedPrescriptions.map((prescription) => [
        prescription.patientName || "",
        prescription.prescriptionName || "Prescription",
        prescription.physicianName || "",
        formatDateOnly(prescription.prescriptionDate),
        prescription.fileName || "",
      ]),
      theme: "grid",
      headStyles: { fillColor: [14, 130, 192] },
      styles: { fontSize: 8, cellPadding: 2 },
    });
  }

  return pdf;
};

const getPrescriptionSignedUrl = async (prescription, currentUser) => {
  if (!prescription.storagePath || !currentUser) return "";
  const idToken = await currentUser.getIdToken();
  const response = await fetch(
    `${API_BASE_URL}/prescriptions/generatePrescriptionSignedUrl`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        filePath: prescription.storagePath,
        careProfileId: prescription.careProfileId,
      }),
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Could not load prescription file.");
  }
  const result = await response.json();
  return result.url || result.signedUrl;
};

const appendPrescriptionContentsToPdf = async (
  basePdfBlob,
  order,
  currentUser,
) => {
  const attachedPrescriptions = order.attachedPrescriptions || [];
  if (attachedPrescriptions.length === 0) return basePdfBlob;

  const pdfDoc = await PDFDocument.load(await basePdfBlob.arrayBuffer());

  for (const prescription of attachedPrescriptions) {
    try {
      if (!prescription.storagePath) {
        await drawPrescriptionPlaceholder(
          pdfDoc,
          prescription,
          "This prescription has no stored file to attach.",
        );
        continue;
      }

      const signedUrl = await getPrescriptionSignedUrl(
        prescription,
        currentUser,
      );
      const fileResponse = await fetch(signedUrl);
      if (!fileResponse.ok) {
        throw new Error("Could not fetch prescription file.");
      }
      const fileBytes = await fileResponse.arrayBuffer();

      if (isPdfPrescription(prescription)) {
        const prescriptionPdf = await PDFDocument.load(fileBytes);
        const copiedPages = await pdfDoc.copyPages(
          prescriptionPdf,
          prescriptionPdf.getPageIndices(),
        );
        copiedPages.forEach((page) => pdfDoc.addPage(page));
        continue;
      }

      if (isImagePrescription(prescription)) {
        const page = pdfDoc.addPage();
        const { width, height } = page.getSize();
        const image =
          getPrescriptionFileExtension(prescription) === "png"
            ? await pdfDoc.embedPng(fileBytes)
            : await pdfDoc.embedJpg(fileBytes);
        const maxWidth = width - 60;
        const maxHeight = height - 80;
        const scale = Math.min(
          maxWidth / image.width,
          maxHeight / image.height,
          1,
        );
        const imageWidth = image.width * scale;
        const imageHeight = image.height * scale;
        page.drawImage(image, {
          x: (width - imageWidth) / 2,
          y: (height - imageHeight) / 2,
          width: imageWidth,
          height: imageHeight,
        });
        continue;
      }

      await drawPrescriptionPlaceholder(
        pdfDoc,
        prescription,
        `Unsupported prescription file type: ${
          prescription.fileName || "unknown file"
        }.`,
      );
    } catch (error) {
      console.error("Error attaching prescription content:", error);
      await drawPrescriptionPlaceholder(
        pdfDoc,
        prescription,
        `Could not attach this prescription file: ${error.message || error}`,
      );
    }
  }

  const mergedBytes = await pdfDoc.save();
  return new Blob([mergedBytes], { type: "application/pdf" });
};

const createMedicationOrderPdfBlob = async (
  order,
  logoBase64 = null,
  currentUser = null,
) => {
  const basePdfBlob = createMedicationOrderPdfDoc(order, logoBase64).output(
    "blob",
  );
  return appendPrescriptionContentsToPdf(basePdfBlob, order, currentUser);
};

const generateMedicationOrderPdf = async (
  order,
  logoBase64 = null,
  currentUser = null,
) => {
  const pdfBlob = await createMedicationOrderPdfBlob(
    order,
    logoBase64,
    currentUser,
  );
  downloadBlob(pdfBlob, getMedicationOrderPdfFilename(order));
};

const shareOrDownloadMedicationOrderPdf = async (
  order,
  logoBase64 = null,
  currentUser = null,
) => {
  const filename = getMedicationOrderPdfFilename(order);
  const pdfBlob = await createMedicationOrderPdfBlob(
    order,
    logoBase64,
    currentUser,
  );
  const file = new File([pdfBlob], filename, { type: "application/pdf" });

  if (
    navigator.share &&
    navigator.canShare &&
    navigator.canShare({ files: [file] })
  ) {
    await navigator.share({
      title: "MILA Care medication order",
      text: "Medication order PDF",
      files: [file],
    });
    return "shared";
  }

  downloadBlob(pdfBlob, filename);
  return "downloaded";
};

const doesSavedOrderItemMatchRow = (savedItem, medicineRow) => {
  if (savedItem.careProfileId !== medicineRow.careProfileId) return false;
  if (
    medicineRow.medicationInventoryId &&
    savedItem.medicationInventoryId === medicineRow.medicationInventoryId
  ) {
    return true;
  }
  if (
    medicineRow.medicationId &&
    savedItem.medicationId === medicineRow.medicationId
  ) {
    return true;
  }
  return (
    !medicineRow.medicationId &&
    !savedItem.medicationId &&
    savedItem.medicationName === medicineRow.medicationName &&
    (savedItem.strength || "") === (medicineRow.strength || "") &&
    (savedItem.form || "") === (medicineRow.form || "")
  );
};

const getLastOrderedQuantity = (medicineRow, orders = []) => {
  for (const order of orders) {
    const savedOrderItem = (order.items || []).find((item) =>
      doesSavedOrderItemMatchRow(item, medicineRow),
    );
    const quantity = toNumber(savedOrderItem?.quantity);
    if (quantity > 0) return quantity;
  }
  return 0;
};

const getOrderItemKey = (item, index = 0) =>
  item.lineId ||
  [
    item.medicationInventoryId || item.medicationId || item.medicationName,
    item.careProfileId,
    index,
  ]
    .filter(Boolean)
    .join("-");

const getFirstVisibleElement = (selector) =>
  Array.from(document.querySelectorAll(selector)).find(
    (element) =>
      !element.disabled &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.offsetParent !== null,
  );

const getOrderReceiptTotalsByLine = (order) => {
  const totals = {};
  (order?.stockReceipts || []).forEach((receipt) => {
    const direction = receipt.type === "remove" ? -1 : 1;
    (receipt.items || []).forEach((item) => {
      const key = item.orderItemKey || item.medicationInventoryId;
      if (!key) return;
      totals[key] = Math.max(
        0,
        (totals[key] || 0) + direction * toNumber(item.quantity),
      );
    });
  });
  return totals;
};

const normalizeMatchValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const doesOrderItemMatchInventoryItem = (orderItem, inventoryItem) => {
  if (!orderItem || !inventoryItem) return false;
  if (orderItem.careProfileId !== inventoryItem.careProfileId) return false;
  if (
    orderItem.medicationInventoryId &&
    orderItem.medicationInventoryId === inventoryItem.id
  ) {
    return true;
  }
  if (
    orderItem.medicationId &&
    orderItem.medicationId === inventoryItem.medicationId
  ) {
    return true;
  }
  const orderMedicationName = normalizeMatchValue(orderItem.medicationName);
  return (
    orderMedicationName &&
    orderMedicationName === normalizeMatchValue(inventoryItem.medicationName) &&
    normalizeMatchValue(orderItem.strength) ===
      normalizeMatchValue(inventoryItem.strength) &&
    normalizeMatchValue(orderItem.form) ===
      normalizeMatchValue(inventoryItem.form)
  );
};

const resolveOrderItemInventoryItem = (orderItem, inventoryById, inventory) => {
  if (orderItem?.medicationInventoryId) {
    const directMatch = inventoryById.get(orderItem.medicationInventoryId);
    if (directMatch) return directMatch;
  }
  return inventory.find((item) =>
    doesOrderItemMatchInventoryItem(orderItem, item),
  );
};

const getOrderReceiptSummary = (order, resolveInventoryItem = null) => {
  const orderItems = order?.items || [];
  const receivableItems = resolveInventoryItem
    ? orderItems
    : orderItems.filter((item) => item.medicationInventoryId);
  if (receivableItems.length === 0) {
    return {
      label: "",
      classes: "",
      receivedTotal: 0,
      orderedTotal: 0,
      hasReceivedStock: false,
      isFullyReceived: false,
    };
  }

  const totalsByLine = getOrderReceiptTotalsByLine(order);
  const orderedTotal = receivableItems.reduce(
    (total, item) => total + toNumber(item.quantity),
    0,
  );
  const receivedTotal = orderItems.reduce((total, item, index) => {
    const isReceivable = resolveInventoryItem
      ? receivableItems.includes(item)
      : item.medicationInventoryId;
    if (!isReceivable) return total;
    const key = getOrderItemKey(item, index);
    return total + toNumber(totalsByLine[key]);
  }, 0);

  if (receivedTotal <= 0) {
    return {
      label: "Stock not received",
      classes: "bg-amber-100 text-amber-700",
      receivedTotal,
      orderedTotal,
      hasReceivedStock: false,
      isFullyReceived: false,
    };
  }

  if (receivedTotal < orderedTotal) {
    return {
      label: "Partially received",
      classes: "bg-blue-100 text-blue-700",
      receivedTotal,
      orderedTotal,
      hasReceivedStock: true,
      isFullyReceived: false,
    };
  }

  return {
    label: "Stock received",
    classes: "bg-green-100 text-green-700",
    receivedTotal,
    orderedTotal,
    hasReceivedStock: true,
    isFullyReceived: true,
  };
};

const MedicationInventoryManager = ({ view = "ordering" }) => {
  const {
    currentUser,
    activeCareProfile,
    ownedProfiles,
    sharedProfiles,
    userRole,
  } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [medications, setMedications] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState(true);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [orderMode, setOrderMode] = useState("single");
  const [selectedProfileIds, setSelectedProfileIds] = useState([]);
  const [inventoryForm, setInventoryForm] = useState(emptyInventoryForm);
  const [editingInventoryId, setEditingInventoryId] = useState(null);
  const [adjustments, setAdjustments] = useState({});
  const [orderDraft, setOrderDraft] = useState(emptyOrderDraft);
  const [orderItems, setOrderItems] = useState([]);
  const [selectedPrescriptionIds, setSelectedPrescriptionIds] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [transactionType, setTransactionType] = useState("normal");
  const [transactionTypesByProfile, setTransactionTypesByProfile] = useState(
    {},
  );
  const [seniorIds, setSeniorIds] = useState({});
  const [isSavingInventory, setIsSavingInventory] = useState(false);
  const [isFinalizingOrder, setIsFinalizingOrder] = useState(false);
  const [isApplyingOrderStock, setIsApplyingOrderStock] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderHistoryView, setOrderHistoryView] = useState("active");
  const [stockActionOrder, setStockActionOrder] = useState(null);
  const [stockActionMode, setStockActionMode] = useState("receive");
  const [stockActionDrafts, setStockActionDrafts] = useState({});
  const [previewOrder, setPreviewOrder] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState("");
  const [previewMode, setPreviewMode] = useState("builder");
  const [logoBase64, setLogoBase64] = useState(null);
  const [showOrderBuilder, setShowOrderBuilder] = useState(false);
  const [showOrderCart, setShowOrderCart] = useState(false);
  const [showInventoryTracking, setShowInventoryTracking] = useState(
    view === "inventory",
  );
  const [showInventorySetupForm, setShowInventorySetupForm] = useState(false);
  const [groupProfilesConfirmed, setGroupProfilesConfirmed] = useState(false);
  const inventoryEditFocusPendingRef = useRef(false);
  const inventoryFocusRestoreIdRef = useRef(null);
  const isInventoryView = view === "inventory";
  const isOrderingView = !isInventoryView;

  const accessibleProfiles = useMemo(() => {
    const byId = new Map();
    [...(ownedProfiles || []), ...(sharedProfiles || [])].forEach((profile) => {
      if (profile?.id) byId.set(profile.id, profile);
    });
    if (activeCareProfile?.id)
      byId.set(activeCareProfile.id, activeCareProfile);
    return Array.from(byId.values()).sort((a, b) =>
      getProfileLabel(a).localeCompare(getProfileLabel(b)),
    );
  }, [activeCareProfile, ownedProfiles, sharedProfiles]);

  const selectedProfiles = useMemo(
    () =>
      accessibleProfiles.filter((profile) =>
        selectedProfileIds.includes(profile.id),
      ),
    [accessibleProfiles, selectedProfileIds],
  );

  const profileById = useMemo(() => {
    const map = new Map();
    accessibleProfiles.forEach((profile) => map.set(profile.id, profile));
    return map;
  }, [accessibleProfiles]);

  const medicationById = useMemo(() => {
    const map = new Map();
    medications.forEach((medication) => map.set(medication.id, medication));
    return map;
  }, [medications]);

  const getRoleForProfile = (profileId) => {
    const profile = profileById.get(profileId);
    return (
      userRole?.[profileId] ||
      profile?.role ||
      (profile?.userId === currentUser?.uid ? ROLES.OWNER : undefined)
    );
  };

  const hasInventoryPermission = (profileId, permission) =>
    hasPermission(
      getRoleForProfile(profileId),
      FEATURES.MEDICATION_INVENTORY,
      permission,
    );

  const canReadSelected =
    selectedProfileIds.length > 0 &&
    selectedProfileIds.every((profileId) =>
      hasInventoryPermission(profileId, "read"),
    );
  const canWriteSelected =
    selectedProfileIds.length > 0 &&
    selectedProfileIds.every((profileId) =>
      hasInventoryPermission(profileId, "write"),
    );

  const canModifyOrder = (order) => {
    const profileIds =
      order?.profileIds || [order?.careProfileId].filter(Boolean);
    return (
      profileIds.length > 0 &&
      profileIds.every((profileId) =>
        hasInventoryPermission(profileId, "write"),
      )
    );
  };

  useEffect(() => {
    if (!activeCareProfile?.id) {
      setSelectedProfileIds([]);
      setInventoryForm(emptyInventoryForm);
      setOrderDraft(emptyOrderDraft);
      return;
    }

    setSelectedProfileIds([activeCareProfile.id]);
    setInventoryForm((prev) => ({ ...prev, profileId: activeCareProfile.id }));
    setOrderDraft((prev) => ({ ...prev, profileId: activeCareProfile.id }));
    setShowOrderBuilder(false);
    setShowOrderCart(false);
    setShowInventoryTracking(isInventoryView);
    setShowInventorySetupForm(false);
    setEditingOrder(null);
    setOrderItems([]);
    setSelectedPrescriptionIds([]);
    setGroupProfilesConfirmed(false);
  }, [activeCareProfile, isInventoryView]);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await fetch("/logo.png");
        if (!response.ok) return;
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Error fetching logo for medication order PDF:", error);
      }
    };

    fetchLogo();
  }, []);

  useEffect(() => {
    setOrderItems((prev) =>
      prev.filter((item) => selectedProfileIds.includes(item.careProfileId)),
    );
  }, [selectedProfileIds]);

  useEffect(() => {
    return () => {
      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
      }
    };
  }, [previewPdfUrl]);

  useEffect(() => {
    if (!canReadSelected) {
      setInventory([]);
      setMedications([]);
      setPrescriptions([]);
      setIsLoadingInventory(false);
      return undefined;
    }

    setIsLoadingInventory(true);
    const chunks = chunkArray(selectedProfileIds, 10);
    const inventoryByChunk = new Map();
    const medicationsByChunk = new Map();
    const prescriptionsByChunk = new Map();

    const syncInventory = () => {
      const rows = Array.from(inventoryByChunk.values()).flat();
      rows.sort((a, b) => {
        const profileA = getProfileLabel(profileById.get(a.careProfileId));
        const profileB = getProfileLabel(profileById.get(b.careProfileId));
        if (profileA !== profileB) return profileA.localeCompare(profileB);
        return (a.medicationName || "").localeCompare(b.medicationName || "");
      });
      setInventory(rows);
      setIsLoadingInventory(false);
    };

    const syncMedications = () => {
      const rows = Array.from(medicationsByChunk.values()).flat();
      rows.sort((a, b) =>
        (a.medicationName || "").localeCompare(b.medicationName || ""),
      );
      setMedications(rows);
    };

    const syncPrescriptions = () => {
      const rows = Array.from(prescriptionsByChunk.values()).flat();
      rows.sort((a, b) => {
        const profileA = getProfileLabel(profileById.get(a.careProfileId));
        const profileB = getProfileLabel(profileById.get(b.careProfileId));
        if (profileA !== profileB) return profileA.localeCompare(profileB);
        const bTime = getDateValue(getPrescriptionDate(b))?.getTime() || 0;
        const aTime = getDateValue(getPrescriptionDate(a))?.getTime() || 0;
        return bTime - aTime;
      });
      setPrescriptions(rows);
    };

    const unsubscribers = chunks.flatMap((ids, index) => {
      const inventoryQuery = query(
        collection(db, "medicationInventory"),
        where("careProfileId", "in", ids),
      );
      const medsQuery = query(
        collection(db, "medications"),
        where("careProfileId", "in", ids),
        where("status", "in", ["Active", "PRN", "Draft"]),
      );
      const prescriptionsQuery = query(
        collection(db, "prescriptions"),
        where("careProfileId", "in", ids),
      );

      const unsubscribeInventory = onSnapshot(
        inventoryQuery,
        (snapshot) => {
          inventoryByChunk.set(
            index,
            snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            })),
          );
          syncInventory();
        },
        (error) => {
          console.error("Error loading medication inventory:", error);
          setIsLoadingInventory(false);
        },
      );

      const unsubscribeMeds = onSnapshot(
        medsQuery,
        (snapshot) => {
          medicationsByChunk.set(
            index,
            snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            })),
          );
          syncMedications();
        },
        (error) => {
          console.error("Error loading medications for inventory:", error);
        },
      );

      const unsubscribePrescriptions = onSnapshot(
        prescriptionsQuery,
        (snapshot) => {
          prescriptionsByChunk.set(
            index,
            snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            })),
          );
          syncPrescriptions();
        },
        (error) => {
          console.error("Error loading prescriptions for ordering:", error);
        },
      );

      return [unsubscribeInventory, unsubscribeMeds, unsubscribePrescriptions];
    });

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [canReadSelected, profileById, selectedProfileIds]);

  useEffect(() => {
    if (!canReadSelected || selectedProfileIds.length === 0) {
      setOrders([]);
      setIsLoadingOrders(false);
      return undefined;
    }

    setIsLoadingOrders(true);
    const chunks = chunkArray(selectedProfileIds, 10);
    const ordersByChunk = new Map();

    const syncOrders = () => {
      const byId = new Map();
      Array.from(ordersByChunk.values())
        .flat()
        .forEach((order) => byId.set(order.id, order));
      const rows = Array.from(byId.values()).sort((a, b) => {
        const aTime = getDateValue(a.finalizedAt)?.getTime() || 0;
        const bTime = getDateValue(b.finalizedAt)?.getTime() || 0;
        return bTime - aTime;
      });
      setOrders(rows);
      setIsLoadingOrders(false);
    };

    const unsubscribers = chunks.map((ids, index) => {
      const ordersQuery = query(
        collection(db, "medicationOrders"),
        where("profileIds", "array-contains-any", ids),
      );
      return onSnapshot(
        ordersQuery,
        (snapshot) => {
          ordersByChunk.set(
            index,
            snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            })),
          );
          syncOrders();
        },
        (error) => {
          console.error("Error loading medication orders:", error);
          setIsLoadingOrders(false);
        },
      );
    });

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [canReadSelected, selectedProfileIds]);

  const summary = useMemo(() => {
    const lowStock = inventory.filter((item) => {
      const status = getStockStatus({
        ...item,
        isInventoryTracked: true,
      }).label;
      return status === "Low stock";
    }).length;
    const outOfStock = inventory.filter(
      (item) =>
        getStockStatus({
          ...item,
          isInventoryTracked: true,
        }).label === "Out of stock",
    ).length;
    return {
      total: inventory.length,
      lowStock,
      outOfStock,
    };
  }, [inventory]);

  const inventoryById = useMemo(() => {
    const map = new Map();
    inventory.forEach((item) => map.set(item.id, item));
    return map;
  }, [inventory]);

  const resolveInventoryItemForOrderItem = (orderItem) =>
    resolveOrderItemInventoryItem(orderItem, inventoryById, inventory);

  const activeOrders = useMemo(
    () => orders.filter((order) => !order.isTrashed),
    [orders],
  );

  const trashedOrders = useMemo(
    () => orders.filter((order) => Boolean(order.isTrashed)),
    [orders],
  );

  const restoreInventoryEditFocus = (inventoryId) => {
    if (!inventoryId) return;

    window.setTimeout(() => {
      const trigger = getFirstVisibleElement(
        `[data-inventory-edit-trigger="${inventoryId}"]`,
      );
      trigger?.scrollIntoView({ behavior: "smooth", block: "center" });
      trigger?.focus({ preventScroll: true });
    }, 0);
  };

  const focusInventoryEditControls = (inventoryId) => {
    if (!inventoryId) return;

    window.setTimeout(() => {
      const editControl = getFirstVisibleElement(
        `[data-stock-edit-panel="${inventoryId}"] select, ` +
          `[data-stock-edit-panel="${inventoryId}"] input, ` +
          `[data-stock-edit-panel="${inventoryId}"] textarea, ` +
          `[data-stock-edit-panel="${inventoryId}"] button`,
      );
      const fallback = document.getElementById("inventory-quantity");
      const target = editControl || fallback;
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus({ preventScroll: true });
    }, 0);
  };

  const resetInventoryForm = ({ restoreFocus = true } = {}) => {
    const inventoryIdToRestore =
      inventoryFocusRestoreIdRef.current || editingInventoryId;
    const defaultProfileId =
      selectedProfileIds[0] || activeCareProfile?.id || "";
    setEditingInventoryId(null);
    setInventoryForm({ ...emptyInventoryForm, profileId: defaultProfileId });
    setShowInventorySetupForm(false);
    inventoryEditFocusPendingRef.current = false;
    inventoryFocusRestoreIdRef.current = null;
    if (restoreFocus) {
      restoreInventoryEditFocus(inventoryIdToRestore);
    }
  };

  useEffect(() => {
    if (
      !editingInventoryId ||
      !showInventorySetupForm ||
      !inventoryEditFocusPendingRef.current
    ) {
      return;
    }

    inventoryEditFocusPendingRef.current = false;
    focusInventoryEditControls(editingInventoryId);
  }, [editingInventoryId, showInventorySetupForm]);

  const handleMedicationTemplateChange = (medicationId) => {
    const selectedMed = medications.find((med) => med.id === medicationId);
    setInventoryForm((prev) => ({
      ...prev,
      medicationId,
      medicationName: selectedMed?.medicationName || prev.medicationName,
      genericName: selectedMed?.genericName || prev.genericName || "",
      strength: selectedMed?.strength || prev.strength,
      form: selectedMed?.form || prev.form,
    }));
  };

  const getActor = () => ({
    uid: currentUser.uid,
    name: currentUser.displayName || currentUser.email,
  });

  const handleSaveInventory = async (event) => {
    event.preventDefault();
    if (!currentUser || !activeCareProfile) return;
    if (!hasInventoryPermission(inventoryForm.profileId, "write")) {
      window.alert("You do not have permission to update inventory.");
      return;
    }
    if (!String(inventoryForm.medicationName || "").trim()) {
      window.alert("Please enter a medicine name.");
      return;
    }

    const duplicate = inventory.find(
      (item) =>
        item.id !== editingInventoryId &&
        item.careProfileId === inventoryForm.profileId &&
        inventoryForm.medicationId &&
        item.medicationId === inventoryForm.medicationId,
    );
    if (duplicate) {
      window.alert(
        "This medicine already has an inventory row for that patient.",
      );
      return;
    }
    const lowStockThreshold = toNumber(inventoryForm.lowStockThreshold, NaN);
    if (
      !Number.isFinite(lowStockThreshold) ||
      lowStockThreshold < 0 ||
      !Number.isInteger(lowStockThreshold)
    ) {
      window.alert("Please enter a whole number for the low stock threshold.");
      return;
    }

    setIsSavingInventory(true);
    const now = Timestamp.now();
    const actor = getActor();
    const profile = profileById.get(inventoryForm.profileId);
    const selectedMedication = inventoryForm.medicationId
      ? medicationById.get(inventoryForm.medicationId)
      : null;
    const existingInventoryItem = editingInventoryId
      ? inventoryById.get(editingInventoryId)
      : null;
    const payload = {
      careProfileId: inventoryForm.profileId,
      userId: profile?.userId || activeCareProfile.userId,
      medicationId: inventoryForm.medicationId || null,
      medicationName: inventoryForm.medicationName.trim(),
      genericName:
        inventoryForm.genericName ||
        selectedMedication?.genericName ||
        existingInventoryItem?.genericName ||
        "",
      strength: inventoryForm.strength || "",
      form: inventoryForm.form || "",
      unitLabel: inventoryForm.unitLabel || "unit",
      quantityOnHand: toNumber(inventoryForm.quantityOnHand),
      deductQuantityPerAdministration: toNumber(
        inventoryForm.deductQuantityPerAdministration,
        1,
      ),
      lowStockThreshold,
      notes: inventoryForm.notes || "",
      lastModifiedAt: now,
      lastModifiedBy: actor,
    };

    try {
      if (editingInventoryId) {
        await updateDoc(
          doc(db, "medicationInventory", editingInventoryId),
          payload,
        );
      } else {
        const createdRef = await addDoc(collection(db, "medicationInventory"), {
          ...payload,
          createdAt: now,
          createdBy: actor,
        });
        if (payload.quantityOnHand !== 0) {
          await addDoc(collection(db, "medicationInventoryAdjustments"), {
            careProfileId: payload.careProfileId,
            medicationInventoryId: createdRef.id,
            medicationId: payload.medicationId,
            medicationName: payload.medicationName,
            source: "initial-count",
            sourceId: createdRef.id,
            delta: payload.quantityOnHand,
            quantityBefore: 0,
            quantityAfter: payload.quantityOnHand,
            reason: "Initial stock count",
            createdAt: now,
            createdBy: actor,
          });
        }
      }
      resetInventoryForm();
    } catch (error) {
      console.error("Error saving medication inventory:", error);
      window.alert("Failed to save inventory.");
    } finally {
      setIsSavingInventory(false);
    }
  };

  const handleEditInventory = (item) => {
    if (!item.isInventoryTracked) return;
    if (editingInventoryId === item.id) {
      resetInventoryForm();
      return;
    }
    inventoryFocusRestoreIdRef.current = item.id;
    inventoryEditFocusPendingRef.current = true;
    setEditingInventoryId(item.id);
    setShowInventorySetupForm(true);
    setInventoryForm({
      profileId: item.careProfileId || activeCareProfile?.id || "",
      medicationId: item.medicationId || "",
      medicationName: item.medicationName || "",
      genericName: item.genericName || "",
      strength: item.strength || "",
      form: item.form || "",
      unitLabel: item.unitLabel || "unit",
      quantityOnHand: String(item.quantityOnHand ?? ""),
      deductQuantityPerAdministration: String(
        item.deductQuantityPerAdministration ?? 1,
      ),
      lowStockThreshold: String(item.lowStockThreshold ?? 0),
      notes: item.notes || "",
    });
  };

  const handleToggleInventoryTracking = async (item, shouldTrack) => {
    if (!hasInventoryPermission(item.careProfileId, "write")) {
      window.alert("You do not have permission to update inventory tracking.");
      return;
    }

    const now = Timestamp.now();
    const actor = getActor();

    if (shouldTrack) {
      try {
        const profile = profileById.get(item.careProfileId);
        const medication = item.medicationId
          ? medicationById.get(item.medicationId)
          : null;
        await addDoc(collection(db, "medicationInventory"), {
          careProfileId: item.careProfileId,
          userId: profile?.userId || activeCareProfile.userId,
          medicationId: item.medicationId || null,
          medicationName: item.medicationName,
          genericName: item.genericName || medication?.genericName || "",
          strength: item.strength || "",
          form: item.form || "",
          unitLabel: item.unitLabel || "unit",
          quantityOnHand: 0,
          deductQuantityPerAdministration: 1,
          lowStockThreshold: 7,
          notes: "",
          createdAt: now,
          createdBy: actor,
          lastModifiedAt: now,
          lastModifiedBy: actor,
        });
      } catch (error) {
        console.error("Error enabling medication tracking:", error);
        window.alert("Failed to start tracking this medicine.");
      }
      return;
    }

    if (!item.medicationInventoryId) return;
    if (
      !window.confirm(
        "Stop tracking this medicine? Its stock count will be removed from inventory.",
      )
    ) {
      return;
    }

    try {
      await deleteDoc(
        doc(db, "medicationInventory", item.medicationInventoryId),
      );
      if (editingInventoryId === item.medicationInventoryId) {
        resetInventoryForm();
      }
    } catch (error) {
      console.error("Error disabling medication tracking:", error);
      window.alert("Failed to stop tracking this medicine.");
    }
  };

  const handleLowStockThresholdChange = async (item, value) => {
    if (!item.isInventoryTracked || !item.medicationInventoryId) return;
    if (!hasInventoryPermission(item.careProfileId, "write")) {
      window.alert("You do not have permission to update inventory tracking.");
      return;
    }

    const nextThreshold = toNumber(value, NaN);
    if (
      !Number.isFinite(nextThreshold) ||
      nextThreshold < 0 ||
      !Number.isInteger(nextThreshold)
    ) {
      window.alert("Please enter a whole number for the low stock threshold.");
      return;
    }
    if (nextThreshold === toNumber(item.lowStockThreshold)) return;

    try {
      await updateDoc(
        doc(db, "medicationInventory", item.medicationInventoryId),
        {
          lowStockThreshold: nextThreshold,
          lastModifiedAt: Timestamp.now(),
          lastModifiedBy: getActor(),
        },
      );
    } catch (error) {
      console.error("Error updating low stock threshold:", error);
      window.alert("Failed to update low stock threshold.");
    }
  };

  const updateAdjustmentDraft = (inventoryId, updates) => {
    setAdjustments((prev) => ({
      ...prev,
      [inventoryId]: {
        ...emptyAdjustment,
        ...(prev[inventoryId] || {}),
        ...updates,
      },
    }));
  };

  const handleApplyAdjustment = async (item) => {
    if (!hasInventoryPermission(item.careProfileId, "write")) {
      window.alert("You do not have permission to adjust this inventory item.");
      return;
    }
    const draft = adjustments[item.id] || emptyAdjustment;
    const quantity = toNumber(draft.quantity, NaN);
    if (!Number.isFinite(quantity) || quantity < 0) {
      window.alert("Please enter a valid adjustment quantity.");
      return;
    }
    if (!String(draft.reason || "").trim()) {
      window.alert("Please enter a reason for this stock change.");
      return;
    }

    const quantityBefore = toNumber(item.quantityOnHand);
    let quantityAfter = quantityBefore;
    if (draft.mode === "set") quantityAfter = quantity;
    if (draft.mode === "add") quantityAfter = quantityBefore + quantity;
    if (draft.mode === "subtract")
      quantityAfter = Math.max(0, quantityBefore - quantity);
    const delta = quantityAfter - quantityBefore;
    const now = Timestamp.now();
    const actor = getActor();

    try {
      await updateDoc(doc(db, "medicationInventory", item.id), {
        quantityOnHand: quantityAfter,
        lastModifiedAt: now,
        lastModifiedBy: actor,
      });
      await addDoc(collection(db, "medicationInventoryAdjustments"), {
        careProfileId: item.careProfileId,
        medicationInventoryId: item.id,
        medicationId: item.medicationId || null,
        medicationName: item.medicationName,
        source: "manual-override",
        sourceId: item.id,
        delta,
        quantityBefore,
        quantityAfter,
        reason: draft.reason.trim(),
        createdAt: now,
        createdBy: actor,
      });
      updateAdjustmentDraft(item.id, emptyAdjustment);
    } catch (error) {
      console.error("Error applying medication inventory adjustment:", error);
      window.alert("Failed to update stock.");
    }
  };

  const handleOrderModeChange = (mode) => {
    setOrderMode(mode);
    setGroupProfilesConfirmed(mode !== "group");
    if (mode === "single" && activeCareProfile?.id) {
      setSelectedProfileIds([activeCareProfile.id]);
      setInventoryForm((prev) => ({
        ...prev,
        profileId: activeCareProfile.id,
      }));
      setOrderDraft((prev) => ({ ...prev, profileId: activeCareProfile.id }));
    }
  };

  const startNewOrder = (mode) => {
    if (!activeCareProfile?.id) return;
    closeOrderPreview();
    setEditingOrder(null);
    setOrderItems([]);
    setSelectedPrescriptionIds([]);
    setShowOrderCart(false);
    setDeliveryAddress("");
    setDeliveryInstructions("");
    setOrderMode(mode);
    setGroupProfilesConfirmed(mode !== "group");

    if (mode === "group") {
      const writableProfileIds = accessibleProfiles
        .filter((profile) => hasInventoryPermission(profile.id, "write"))
        .map((profile) => profile.id);
      const nextProfileIds =
        writableProfileIds.length > 0
          ? writableProfileIds
          : [activeCareProfile.id];
      setSelectedProfileIds(nextProfileIds);
      setInventoryForm((prev) => ({
        ...prev,
        profileId: nextProfileIds[0],
      }));
      setOrderDraft((prev) => ({
        ...prev,
        profileId: nextProfileIds[0],
      }));
    } else {
      setSelectedProfileIds([activeCareProfile.id]);
      setInventoryForm((prev) => ({
        ...prev,
        profileId: activeCareProfile.id,
      }));
      setOrderDraft((prev) => ({
        ...prev,
        profileId: activeCareProfile.id,
      }));
    }

    setShowOrderBuilder(true);
    if (mode !== "group") {
      window.setTimeout(() => {
        document
          .getElementById("medication-order-builder")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  const confirmGroupProfiles = () => {
    if (selectedProfileIds.length === 0) {
      window.alert("Select at least one profile for this group order.");
      return;
    }
    setGroupProfilesConfirmed(true);
    setShowOrderCart(false);
    window.setTimeout(() => {
      document
        .getElementById("medication-order-builder")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const toggleGroupProfile = (profileId, checked) => {
    if (orderMode === "group") {
      setGroupProfilesConfirmed(false);
      setShowOrderCart(false);
    }
    setSelectedProfileIds((prev) => {
      if (checked) {
        return prev.includes(profileId) ? prev : [...prev, profileId];
      }
      const next = prev.filter((id) => id !== profileId);
      return next.length > 0 ? next : prev;
    });
  };

  const getOrderItemForMedicineRow = (medicineRow) =>
    orderItems.find((item) => {
      if (medicineRow.medicationInventoryId) {
        return item.medicationInventoryId === medicineRow.medicationInventoryId;
      }
      return (
        !item.medicationInventoryId &&
        item.medicationId === medicineRow.medicationId &&
        item.careProfileId === medicineRow.careProfileId
      );
    });

  const upsertMedicineOrderItem = (medicineRow, quantityValue) => {
    if (!hasInventoryPermission(medicineRow.careProfileId, "write")) {
      window.alert("You do not have permission to order for this patient.");
      return;
    }

    const quantity = Math.max(0, toNumber(quantityValue));
    setOrderItems((prev) => {
      const existing = prev.find((item) => {
        if (medicineRow.medicationInventoryId) {
          return (
            item.medicationInventoryId === medicineRow.medicationInventoryId
          );
        }
        return (
          !item.medicationInventoryId &&
          item.medicationId === medicineRow.medicationId &&
          item.careProfileId === medicineRow.careProfileId
        );
      });
      if (quantity <= 0) {
        return prev.filter((item) => item.lineId !== existing?.lineId);
      }
      if (existing) {
        return prev.map((item) =>
          item.lineId === existing.lineId
            ? {
                ...item,
                genericName: medicineRow.genericName || item.genericName || "",
                quantity,
              }
            : item,
        );
      }
      return [
        ...prev,
        {
          lineId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          careProfileId: medicineRow.careProfileId,
          medicationInventoryId: medicineRow.medicationInventoryId || null,
          medicationId: medicineRow.medicationId || null,
          medicationName: medicineRow.medicationName,
          genericName: medicineRow.genericName || "",
          useGenericAlternative: false,
          strength: medicineRow.strength || "",
          form: medicineRow.form || "",
          quantity,
          unitLabel: medicineRow.unitLabel || "unit",
          notes: "",
          isNewMedication: false,
        },
      ];
    });
  };

  const updateOrderItemNotes = (lineId, notes) => {
    setOrderItems((prev) =>
      prev.map((item) => (item.lineId === lineId ? { ...item, notes } : item)),
    );
  };

  const toggleOrderItemGeneric = (lineId) => {
    setOrderItems((prev) =>
      prev.map((item) =>
        item.lineId === lineId
          ? {
              ...item,
              useGenericAlternative:
                getGenericName(item) && !item.useGenericAlternative,
              addGenericToMedications: false,
            }
          : item,
      ),
    );
  };

  const toggleAddGenericToMedications = (lineId) => {
    setOrderItems((prev) =>
      prev.map((item) =>
        item.lineId === lineId
          ? {
              ...item,
              addGenericToMedications:
                !!item.useGenericAlternative &&
                getGenericName(item) &&
                !item.addGenericToMedications,
            }
          : item,
      ),
    );
  };

  const addOrderItem = () => {
    if (!orderDraft.profileId) {
      window.alert("Please select a patient for this order line.");
      return;
    }
    if (!hasInventoryPermission(orderDraft.profileId, "write")) {
      window.alert("You do not have permission to order for this patient.");
      return;
    }

    const selectedInventory = inventory.find(
      (item) => item.id === orderDraft.inventoryId,
    );
    const medicationName =
      selectedInventory?.medicationName ||
      String(orderDraft.customMedicationName || "").trim();
    const selectedMedication = selectedInventory?.medicationId
      ? medicationById.get(selectedInventory.medicationId)
      : null;
    if (!medicationName) {
      window.alert("Please select a medicine or enter a new medicine name.");
      return;
    }
    const quantity = toNumber(orderDraft.quantity);
    if (quantity <= 0) {
      window.alert("Please enter an order quantity greater than zero.");
      return;
    }

    const newItem = {
      lineId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      careProfileId: orderDraft.profileId,
      medicationInventoryId: selectedInventory?.id || null,
      medicationId: selectedInventory?.medicationId || null,
      medicationName,
      genericName:
        selectedInventory?.genericName || selectedMedication?.genericName || "",
      useGenericAlternative: false,
      addGenericToMedications: false,
      strength: selectedInventory?.strength || orderDraft.customStrength || "",
      form: selectedInventory?.form || orderDraft.customForm || "",
      quantity,
      unitLabel: selectedInventory?.unitLabel || orderDraft.unitLabel || "unit",
      notes: orderDraft.notes || "",
      isNewMedication: !selectedInventory,
    };
    setOrderItems((prev) => [...prev, newItem]);
    setOrderDraft({
      ...emptyOrderDraft,
      profileId: orderDraft.profileId,
    });
  };

  const updateOrderItemQuantity = (lineId, delta) => {
    setOrderItems((prev) =>
      prev
        .map((item) =>
          item.lineId === lineId
            ? {
                ...item,
                quantity: Math.max(0, toNumber(item.quantity) + delta),
              }
            : item,
        )
        .filter((item) => toNumber(item.quantity) > 0),
    );
  };

  const removeOrderItem = (lineId) => {
    setOrderItems((prev) => prev.filter((item) => item.lineId !== lineId));
  };

  const handleProceedToOrderCart = () => {
    if (orderItems.length === 0) {
      window.alert("Add at least one medicine before proceeding.");
      return;
    }
    setShowOrderCart(true);
    window.setTimeout(() => {
      document
        .getElementById("medication-order-cart")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const buildOrderPayload = () => {
    const profileIds = Array.from(
      new Set(orderItems.map((item) => item.careProfileId)),
    );
    const profileSummaries = profileIds.map((profileId) => {
      const profile = profileById.get(profileId);
      return {
        careProfileId: profileId,
        patientName: getProfileLabel(profile),
        seniorId: getSeniorId(profile, seniorIds),
        transactionType: getProfileTransactionType(
          profileId,
          transactionTypesByProfile,
        ),
      };
    });
    const items = orderItems.map((item) => {
      const profile = profileById.get(item.careProfileId);
      return {
        lineId: item.lineId,
        careProfileId: item.careProfileId,
        patientName: getProfileLabel(profile),
        seniorId: getSeniorId(profile, seniorIds),
        transactionType: getProfileTransactionType(
          item.careProfileId,
          transactionTypesByProfile,
        ),
        medicationInventoryId: item.medicationInventoryId,
        medicationId: item.medicationId,
        medicationName: item.medicationName,
        genericName: item.genericName || "",
        useGenericAlternative: !!item.useGenericAlternative,
        addGenericToMedications: !!item.addGenericToMedications,
        linkedInventoryId: item.linkedInventoryId || null,
        sourceMedicationId: item.sourceMedicationId || null,
        sourceMedicationName: item.sourceMedicationName || "",
        strength: item.strength,
        form: item.form,
        quantity: toNumber(item.quantity),
        unitLabel: item.unitLabel || "unit",
        notes: item.notes || "",
        isNewMedication: !!item.isNewMedication,
      };
    });
    const attachedPrescriptions = prescriptions
      .filter((prescription) =>
        selectedPrescriptionIds.includes(prescription.id),
      )
      .map((prescription) => {
        const profile = profileById.get(prescription.careProfileId);
        return {
          id: prescription.id,
          careProfileId: prescription.careProfileId,
          patientName: getProfileLabel(profile),
          prescriptionName: getPrescriptionName(prescription),
          physicianName: prescription.physicianName || "",
          prescriptionDate: getPrescriptionDate(prescription) || null,
          description: prescription.description || "",
          fileName: prescription.fileName || "",
          storagePath: prescription.storagePath || "",
        };
      });

    const now = Timestamp.now();
    const orderProfile = profileSummaries[0];
    return {
      orderMode: profileIds.length > 1 ? "group" : orderMode,
      careProfileId: profileIds[0],
      profileIds,
      patientName:
        profileSummaries.length === 1
          ? orderProfile?.patientName || ""
          : "Consolidated group order",
      deliveryAddress: deliveryAddress.trim(),
      deliveryInstructions: deliveryInstructions.trim(),
      seniorId:
        profileSummaries.length === 1 ? orderProfile?.seniorId || "" : "",
      transactionType:
        profileSummaries.length === 1
          ? profileSummaries[0]?.transactionType || transactionType
          : "group",
      transactionTypesByProfile,
      status: "finalized",
      profileSummaries,
      items,
      attachedPrescriptionIds: attachedPrescriptions.map(
        (prescription) => prescription.id,
      ),
      attachedPrescriptions,
      finalizedAt: now,
      createdAt: editingOrder?.createdAt || now,
      createdBy: editingOrder?.createdBy || getActor(),
      lastModifiedAt: now,
      lastModifiedBy: getActor(),
      userId: currentUser.uid,
    };
  };

  const handleFinalizeOrder = async () => {
    if (orderItems.length === 0) {
      window.alert("Add at least one medicine before finalizing the order.");
      return;
    }
    if (!canWriteSelected) {
      window.alert("You do not have permission to finalize this order.");
      return;
    }

    try {
      const payload = sanitizeForFirestore(buildOrderPayload());
      const pdfBlob = await createMedicationOrderPdfBlob(
        payload,
        logoBase64,
        currentUser,
      );
      const nextPdfUrl = URL.createObjectURL(pdfBlob);
      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
      }
      setPreviewOrder(payload);
      setPreviewPdfUrl(nextPdfUrl);
      setPreviewMode("builder");
    } catch (error) {
      console.error("Error preparing medication order preview:", error);
      window.alert(`Failed to prepare PDF preview: ${error.message || error}`);
    }
  };

  const closeOrderPreview = () => {
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl("");
    setPreviewOrder(null);
    setPreviewMode("builder");
  };

  const createInventoryItemForOrderItem = async (
    item,
    now,
    actor,
    source = "order",
  ) => {
    const profile = profileById.get(item.careProfileId);
    const medication = item.medicationId
      ? medicationById.get(item.medicationId)
      : null;
    const inventoryPayload = {
      careProfileId: item.careProfileId,
      userId: profile?.userId || activeCareProfile.userId,
      medicationId: item.medicationId || null,
      medicationName: item.medicationName,
      genericName: item.genericName || medication?.genericName || "",
      strength: item.strength || "",
      form: item.form || "",
      unitLabel: item.unitLabel || "unit",
      quantityOnHand: 0,
      deductQuantityPerAdministration: 1,
      lowStockThreshold: 7,
      notes:
        source === "receipt"
          ? "Started from medication order receipt."
          : "Started from medication order.",
      createdAt: now,
      createdBy: actor,
      lastModifiedAt: now,
      lastModifiedBy: actor,
    };
    const createdRef = await addDoc(
      collection(db, "medicationInventory"),
      sanitizeForFirestore(inventoryPayload),
    );
    return {
      id: createdRef.id,
      ...inventoryPayload,
    };
  };

  const ensureInventoryRecordsForOrder = async (order) => {
    const actor = getActor();
    const now = Timestamp.now();
    const createdInventoryItems = [];
    let orderItemsChanged = false;

    const nextItems = [];
    for (const item of order.items || []) {
      const inventoryItem = resolveOrderItemInventoryItem(item, inventoryById, [
        ...inventory,
        ...createdInventoryItems,
      ]);
      if (inventoryItem) {
        if (item.medicationInventoryId === inventoryItem.id) {
          nextItems.push(item);
        } else {
          orderItemsChanged = true;
          nextItems.push({
            ...item,
            medicationInventoryId: inventoryItem.id,
            medicationId:
              inventoryItem.medicationId || item.medicationId || null,
          });
        }
        continue;
      }

      const createdInventoryItem = await createInventoryItemForOrderItem(
        item,
        now,
        actor,
      );
      createdInventoryItems.push(createdInventoryItem);
      orderItemsChanged = true;
      nextItems.push({
        ...item,
        medicationInventoryId: createdInventoryItem.id,
        medicationId:
          createdInventoryItem.medicationId || item.medicationId || null,
      });
    }

    return orderItemsChanged ? { ...order, items: nextItems } : order;
  };

  const createGenericMedicationRecordsForOrder = async (order) => {
    const actor = getActor();
    const now = Timestamp.now();
    const existingMedicationNamesByProfile = medications.reduce(
      (acc, medication) => {
        const profileId = medication.careProfileId;
        if (!profileId) return acc;
        if (!acc[profileId]) acc[profileId] = new Set();
        acc[profileId].add(
          String(medication.medicationName || "")
            .trim()
            .toLowerCase(),
        );
        return acc;
      },
      {},
    );

    const nextItems = await Promise.all(
      (order.items || []).map(async (item) => {
        const genericName = getGenericName(item);
        if (
          !item.useGenericAlternative ||
          !item.addGenericToMedications ||
          !genericName ||
          item.linkedInventoryId
        ) {
          return item;
        }

        const profileNames =
          existingMedicationNamesByProfile[item.careProfileId] || new Set();
        if (profileNames.has(genericName.toLowerCase())) {
          return item;
        }

        const sourceMedication = item.medicationId
          ? medicationById.get(item.medicationId)
          : null;
        const inventoryItem = item.medicationInventoryId
          ? inventoryById.get(item.medicationInventoryId)
          : null;
        const requiredFields = {
          strength: sourceMedication?.strength || item.strength || "",
          form: sourceMedication?.form || item.form || "",
          route: sourceMedication?.route || "",
          frequency: sourceMedication?.frequency || "",
        };
        const hasRequiredFields = Object.values(requiredFields).every(Boolean);
        const status =
          hasRequiredFields &&
          ["Active", "PRN"].includes(sourceMedication?.status)
            ? sourceMedication.status
            : "Draft";

        const createdRef = await addDoc(collection(db, "medications"), {
          medicationName: genericName,
          genericName: item.medicationName || "",
          ...requiredFields,
          instructions: sourceMedication?.instructions || "",
          startDate: sourceMedication?.startDate || now,
          endDate: sourceMedication?.endDate || null,
          notes: sourceMedication?.notes || "",
          status,
          careProfileId: item.careProfileId,
          userId: inventoryItem?.userId || activeCareProfile.userId,
          sourceMedicationId: item.medicationId || null,
          sourceMedicationName: item.medicationName || "",
          linkedInventoryId: item.medicationInventoryId || null,
          inventoryAliasOfMedicationId: item.medicationId || null,
          createdAt: now,
          createdBy: actor,
          lastModifiedAt: now,
          lastModifiedBy: actor,
        });

        profileNames.add(genericName.toLowerCase());
        existingMedicationNamesByProfile[item.careProfileId] = profileNames;

        return {
          ...item,
          linkedGenericMedicationId: createdRef.id,
          linkedInventoryId: item.medicationInventoryId || null,
          sourceMedicationId: item.medicationId || null,
          sourceMedicationName: item.medicationName || "",
        };
      }),
    );

    return {
      ...order,
      items: nextItems,
    };
  };

  const savePreviewOrder = async ({ exportPdf = false } = {}) => {
    if (!previewOrder) return;

    setIsFinalizingOrder(true);
    try {
      const orderWithInventory =
        await ensureInventoryRecordsForOrder(previewOrder);
      const orderToSave =
        await createGenericMedicationRecordsForOrder(orderWithInventory);
      let savedOrderId = editingOrder?.id || null;
      if (editingOrder?.id) {
        await updateDoc(
          doc(db, "medicationOrders", editingOrder.id),
          orderToSave,
        );
      } else {
        const orderRef = await addDoc(
          collection(db, "medicationOrders"),
          orderToSave,
        );
        savedOrderId = orderRef.id;
      }

      if (exportPdf) {
        await generateMedicationOrderPdf(
          { ...orderToSave, id: savedOrderId },
          logoBase64,
          currentUser,
        );
      }

      setOrderItems([]);
      setSelectedPrescriptionIds([]);
      setShowOrderCart(false);
      setDeliveryAddress("");
      setDeliveryInstructions("");
      setEditingOrder(null);
      setShowOrderBuilder(false);
      closeOrderPreview();
    } catch (error) {
      console.error("Error saving medication order:", error);
      window.alert(`Failed to save order: ${error.message || error}`);
    } finally {
      setIsFinalizingOrder(false);
    }
  };

  const exportPreviewPdfOnly = async () => {
    if (!previewOrder) return;
    try {
      await generateMedicationOrderPdf(previewOrder, logoBase64, currentUser);
    } catch (error) {
      console.error("Error exporting medication order PDF:", error);
      window.alert(`Failed to export PDF: ${error.message || error}`);
    }
  };

  const handleForwardOrderPdf = async (order) => {
    try {
      const result = await shareOrDownloadMedicationOrderPdf(
        order,
        logoBase64,
        currentUser,
      );
      if (result === "downloaded") {
        window.alert(
          "PDF sharing is not available in this browser, so the PDF was downloaded instead.",
        );
      }
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Error forwarding medication order PDF:", error);
      window.alert(`Failed to prepare PDF: ${error.message || error}`);
    }
  };

  const handleOpenOrderPdf = async (order) => {
    try {
      const pdfBlob = await createMedicationOrderPdfBlob(
        order,
        logoBase64,
        currentUser,
      );
      const nextPdfUrl = URL.createObjectURL(pdfBlob);
      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
      }
      setPreviewOrder(order);
      setPreviewPdfUrl(nextPdfUrl);
      setPreviewMode("history");
    } catch (error) {
      console.error("Error opening medication order PDF:", error);
      window.alert(`Failed to open PDF: ${error.message || error}`);
    }
  };

  const handleOpenPrescriptionAttachment = async (prescription) => {
    if (!prescription.storagePath) {
      window.alert("This prescription does not have a stored attachment.");
      return;
    }
    const openedWindow = window.open("", "_blank", "noopener,noreferrer");
    try {
      const signedUrl = await getPrescriptionSignedUrl(
        prescription,
        currentUser,
      );
      if (openedWindow) {
        openedWindow.location.href = signedUrl;
      } else {
        window.location.href = signedUrl;
      }
    } catch (error) {
      if (openedWindow) {
        openedWindow.close();
      }
      console.error("Error opening prescription attachment:", error);
      window.alert(`Failed to open attachment: ${error.message || error}`);
    }
  };

  const handleCopyOrderTextNotes = async (order) => {
    const text = buildMedicationOrderTextNotes(order);
    try {
      await navigator.clipboard.writeText(text);
      window.alert(
        "Text notes copied. You can paste them directly into Viber or another messaging app.",
      );
    } catch (error) {
      console.error("Error copying medication order text:", error);
      window.alert(`Failed to copy text notes: ${error.message || error}`);
    }
  };

  const openOrderStockAction = (order, mode = "receive") => {
    if (!canModifyOrder(order)) {
      window.alert("You do not have permission to update inventory.");
      return;
    }

    const totalsByLine = getOrderReceiptTotalsByLine(order);
    const nextDrafts = {};
    (order.items || []).forEach((item, index) => {
      const key = getOrderItemKey(item, index);
      const inventoryItem = resolveInventoryItemForOrderItem(item);
      if (!inventoryItem && mode === "remove") {
        nextDrafts[key] = "";
        return;
      }

      const orderedQuantity = toNumber(item.quantity);
      const receivedQuantity = toNumber(totalsByLine[key]);
      nextDrafts[key] =
        mode === "remove"
          ? String(receivedQuantity)
          : String(Math.max(0, orderedQuantity - receivedQuantity));
    });

    setStockActionOrder(order);
    setStockActionMode(mode);
    setStockActionDrafts(nextDrafts);
  };

  const closeOrderStockAction = () => {
    setStockActionOrder(null);
    setStockActionDrafts({});
    setStockActionMode("receive");
  };

  const updateStockActionDraft = (lineKey, value) => {
    setStockActionDrafts((prev) => ({
      ...prev,
      [lineKey]: value,
    }));
  };

  const fillStockActionRemainingQuantities = () => {
    setStockActionDrafts((prev) => {
      const next = { ...prev };
      stockActionLines.forEach((line) => {
        if (stockActionMode === "remove" && !line.inventoryItem) return;
        next[line.key] =
          stockActionMode === "remove"
            ? String(line.receivedQuantity)
            : String(line.remainingQuantity);
      });
      return next;
    });
  };

  const applyOrderStockMovement = async () => {
    if (!stockActionOrder || isApplyingOrderStock) return;
    if (!canModifyOrder(stockActionOrder)) {
      window.alert("You do not have permission to update inventory.");
      return;
    }
    if (
      stockActionMode === "remove" &&
      !window.confirm(
        "Remove these received quantities from inventory? Stock will never go below zero.",
      )
    ) {
      return;
    }

    const actor = getActor();
    const now = Timestamp.now();
    const receiptItems = [];
    const createdInventoryItems = [];
    const nextOrderItems = (stockActionOrder.items || []).map((item) => ({
      ...item,
    }));
    let orderItemsChanged = false;

    setIsApplyingOrderStock(true);
    try {
      for (const [index, item] of (stockActionOrder.items || []).entries()) {
        const orderItemKey = getOrderItemKey(item, index);
        const requestedQuantity = toNumber(stockActionDrafts[orderItemKey]);
        if (requestedQuantity <= 0) continue;

        let inventoryItem = resolveOrderItemInventoryItem(item, inventoryById, [
          ...inventory,
          ...createdInventoryItems,
        ]);
        if (!inventoryItem && stockActionMode === "receive") {
          inventoryItem = await createInventoryItemForOrderItem(
            item,
            now,
            actor,
            "receipt",
          );
          createdInventoryItems.push(inventoryItem);
        }
        if (!inventoryItem) continue;

        if (nextOrderItems[index]?.medicationInventoryId !== inventoryItem.id) {
          nextOrderItems[index] = {
            ...nextOrderItems[index],
            medicationInventoryId: inventoryItem.id,
            medicationId:
              inventoryItem.medicationId || item.medicationId || null,
          };
          orderItemsChanged = true;
        }

        const quantityBefore = toNumber(inventoryItem.quantityOnHand);
        const quantityAfter =
          stockActionMode === "remove"
            ? Math.max(0, quantityBefore - requestedQuantity)
            : quantityBefore + requestedQuantity;
        const appliedQuantity = Math.abs(quantityAfter - quantityBefore);
        if (appliedQuantity <= 0) continue;

        await updateDoc(doc(db, "medicationInventory", inventoryItem.id), {
          quantityOnHand: quantityAfter,
          lastModifiedAt: now,
          lastModifiedBy: actor,
        });

        await addDoc(collection(db, "medicationInventoryAdjustments"), {
          careProfileId: inventoryItem.careProfileId,
          medicationInventoryId: inventoryItem.id,
          medicationId: inventoryItem.medicationId || item.medicationId || null,
          medicationName: inventoryItem.medicationName || item.medicationName,
          source:
            stockActionMode === "remove"
              ? "medicationOrderRemoved"
              : "medicationOrderReceived",
          sourceId: stockActionOrder.id,
          medicationOrderId: stockActionOrder.id,
          orderItemKey,
          delta: quantityAfter - quantityBefore,
          quantityBefore,
          quantityAfter,
          reason:
            stockActionMode === "remove"
              ? "Stock removed from medication order history"
              : "Stock received from medication order history",
          createdAt: now,
          createdBy: actor,
        });

        receiptItems.push({
          orderItemKey,
          medicationInventoryId: inventoryItem.id,
          medicationId: inventoryItem.medicationId || item.medicationId || null,
          medicationName: inventoryItem.medicationName || item.medicationName,
          quantity: appliedQuantity,
          requestedQuantity,
          quantityBefore,
          quantityAfter,
        });
      }

      if (receiptItems.length === 0) {
        window.alert("Enter a quantity for at least one ordered item.");
        return;
      }

      const nextReceipts = [
        ...(stockActionOrder.stockReceipts || []),
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          type: stockActionMode,
          items: receiptItems,
          createdAt: now,
          createdBy: actor,
        },
      ];
      await updateDoc(doc(db, "medicationOrders", stockActionOrder.id), {
        ...(orderItemsChanged
          ? { items: sanitizeForFirestore(nextOrderItems) }
          : {}),
        stockReceipts: sanitizeForFirestore(nextReceipts),
        stockLastAdjustedAt: now,
        stockLastAdjustedBy: actor,
        ...(stockActionMode === "receive"
          ? {
              stockReceivedAt: now,
              stockReceivedBy: actor,
            }
          : {}),
        lastModifiedAt: now,
        lastModifiedBy: actor,
      });

      closeOrderStockAction();
    } catch (error) {
      console.error("Error applying order stock movement:", error);
      window.alert("Failed to update inventory from this order.");
    } finally {
      setIsApplyingOrderStock(false);
    }
  };

  const handleEditOrder = (order) => {
    if (!canModifyOrder(order)) {
      window.alert("You do not have permission to edit this order.");
      return;
    }
    const profileIds =
      order.profileIds || [order.careProfileId].filter(Boolean);
    if (profileIds.length === 0) return;

    setEditingOrder(order);
    setShowOrderBuilder(true);
    setShowOrderCart(true);
    setSelectedPrescriptionIds(order.attachedPrescriptionIds || []);
    setOrderMode(profileIds.length > 1 ? "group" : "single");
    setGroupProfilesConfirmed(true);
    setSelectedProfileIds(profileIds);
    setDeliveryAddress(order.deliveryAddress || "");
    setDeliveryInstructions(order.deliveryInstructions || "");
    setTransactionType(order.transactionType || "normal");
    setTransactionTypesByProfile(() => {
      const next = { ...(order.transactionTypesByProfile || {}) };
      (order.profileSummaries || []).forEach((profile) => {
        if (profile.careProfileId && profile.transactionType) {
          next[profile.careProfileId] = profile.transactionType;
        }
      });
      return next;
    });
    setSeniorIds((prev) => {
      const next = { ...prev };
      (order.profileSummaries || []).forEach((profile) => {
        if (profile.careProfileId && profile.seniorId) {
          next[profile.careProfileId] = profile.seniorId;
        }
      });
      return next;
    });
    setOrderItems(
      (order.items || []).map((item, index) => ({
        lineId: item.lineId || `${order.id}-${index}`,
        careProfileId: item.careProfileId,
        medicationInventoryId: item.medicationInventoryId || null,
        medicationId: item.medicationId || null,
        medicationName: item.medicationName,
        genericName: item.genericName || "",
        useGenericAlternative: !!item.useGenericAlternative,
        addGenericToMedications: !!item.addGenericToMedications,
        strength: item.strength || "",
        form: item.form || "",
        quantity: toNumber(item.quantity),
        unitLabel: item.unitLabel || "unit",
        notes: item.notes || "",
        isNewMedication: !!item.isNewMedication,
      })),
    );
    setOrderDraft({
      ...emptyOrderDraft,
      profileId: profileIds[0],
    });

    window.setTimeout(() => {
      document
        .getElementById("medication-order-builder")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleCancelOrderEdit = () => {
    setEditingOrder(null);
    setOrderItems([]);
    setSelectedPrescriptionIds([]);
    setShowOrderCart(false);
    setDeliveryAddress("");
    setDeliveryInstructions("");
    setShowOrderBuilder(false);
  };

  const handleCancelOrderBuilder = () => {
    const hasDraft =
      orderItems.length > 0 ||
      String(deliveryAddress || "").trim() ||
      String(deliveryInstructions || "").trim();
    if (
      hasDraft &&
      !window.confirm("Cancel this order and discard the current draft?")
    ) {
      return;
    }

    closeOrderPreview();
    setEditingOrder(null);
    setOrderItems([]);
    setSelectedPrescriptionIds([]);
    setShowOrderCart(false);
    setDeliveryAddress("");
    setDeliveryInstructions("");
    setOrderDraft({
      ...emptyOrderDraft,
      profileId: activeCareProfile?.id || "",
    });
    setOrderMode("single");
    setGroupProfilesConfirmed(false);
    if (activeCareProfile?.id) {
      setSelectedProfileIds([activeCareProfile.id]);
      setInventoryForm((prev) => ({
        ...prev,
        profileId: activeCareProfile.id,
      }));
    }
    setShowOrderBuilder(false);
  };

  const handleMoveOrderToTrash = async (order) => {
    if (!canModifyOrder(order)) {
      window.alert("You do not have permission to move this order.");
      return;
    }

    try {
      const now = Timestamp.now();
      const actor = getActor();
      await updateDoc(doc(db, "medicationOrders", order.id), {
        isTrashed: true,
        trashedAt: now,
        trashedBy: actor,
        lastModifiedAt: now,
        lastModifiedBy: actor,
      });
      if (editingOrder?.id === order.id) {
        handleCancelOrderEdit();
      }
    } catch (error) {
      console.error("Error moving medication order to trash:", error);
      window.alert("Failed to move order to Trash.");
    }
  };

  const handleRestoreOrder = async (order) => {
    if (!canModifyOrder(order)) {
      window.alert("You do not have permission to restore this order.");
      return;
    }

    try {
      const now = Timestamp.now();
      const actor = getActor();
      await updateDoc(doc(db, "medicationOrders", order.id), {
        isTrashed: false,
        trashedAt: null,
        trashedBy: null,
        lastModifiedAt: now,
        lastModifiedBy: actor,
      });
    } catch (error) {
      console.error("Error restoring medication order:", error);
      window.alert("Failed to restore order.");
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!canModifyOrder(order)) {
      window.alert("You do not have permission to delete this order.");
      return;
    }

    const receiptSummary = getOrderReceiptSummary(
      order,
      resolveInventoryItemForOrderItem,
    );
    const message = receiptSummary.hasReceivedStock
      ? "Permanently delete this received order record? Inventory adjustment history will remain, but the order record cannot be restored."
      : "Permanently delete this order history record? This cannot be undone.";
    if (!window.confirm(message)) return;

    try {
      await deleteDoc(doc(db, "medicationOrders", order.id));
    } catch (error) {
      console.error("Error deleting medication order:", error);
      window.alert("Failed to permanently delete order.");
    }
  };

  const inventoryByProfile = useMemo(() => {
    return selectedProfiles.map((profile) => {
      const inventoryItems = inventory
        .filter((item) => item.careProfileId === profile.id)
        .map((item) => {
          const medication = medicationById.get(item.medicationId);
          return {
            ...item,
            genericName: item.genericName || medication?.genericName || "",
            rowId: `inventory-${item.id}`,
            medicationInventoryId: item.id,
            isInventoryTracked: true,
          };
        });
      const inventoryMedicationIds = new Set(
        inventoryItems
          .map((item) => item.medicationId)
          .filter((medicationId) => !!medicationId),
      );
      const medicationOnlyItems = medications
        .filter(
          (medication) =>
            medication.careProfileId === profile.id &&
            !inventoryMedicationIds.has(medication.id),
        )
        .map((medication) => ({
          rowId: `medication-${medication.id}`,
          careProfileId: medication.careProfileId,
          medicationInventoryId: null,
          medicationId: medication.id,
          medicationName: medication.medicationName,
          genericName: medication.genericName || "",
          strength: medication.strength || "",
          form: medication.form || "",
          unitLabel: "unit",
          quantityOnHand: null,
          deductQuantityPerAdministration: 1,
          lowStockThreshold: 7,
          isInventoryTracked: false,
        }));

      return {
        profile,
        items: [...inventoryItems, ...medicationOnlyItems].sort((a, b) =>
          (a.medicationName || "").localeCompare(b.medicationName || ""),
        ),
      };
    });
  }, [inventory, medicationById, medications, selectedProfiles]);

  const orderRowsByProfile = useMemo(() => {
    const savedOrderItems = activeOrders.flatMap((order) => order.items || []);

    return selectedProfiles.map((profile) => {
      const inventoryItems = inventory
        .filter((item) => item.careProfileId === profile.id)
        .map((item) => {
          const medication = medicationById.get(item.medicationId);
          const row = {
            ...item,
            genericName: item.genericName || medication?.genericName || "",
            rowId: `inventory-${item.id}`,
            medicationInventoryId: item.id,
            isInventoryTracked: true,
          };
          const savedOrderItem = savedOrderItems.find((savedItem) =>
            doesSavedOrderItemMatchRow(savedItem, row),
          );
          return {
            ...row,
            genericName: row.genericName || savedOrderItem?.genericName || "",
          };
        });
      const inventoryMedicationIds = new Set(
        inventoryItems
          .map((item) => item.medicationId)
          .filter((medicationId) => !!medicationId),
      );
      const medicationOnlyItems = medications
        .filter(
          (medication) =>
            medication.careProfileId === profile.id &&
            !inventoryMedicationIds.has(medication.id),
        )
        .map((medication) => ({
          rowId: `medication-${medication.id}`,
          careProfileId: medication.careProfileId,
          medicationInventoryId: null,
          medicationId: medication.id,
          medicationName: medication.medicationName,
          genericName: medication.genericName || "",
          strength: medication.strength || "",
          form: medication.form || "",
          unitLabel: "unit",
          quantityOnHand: null,
          lowStockThreshold: null,
          isInventoryTracked: false,
        }));

      return {
        profile,
        items: [...inventoryItems, ...medicationOnlyItems].sort((a, b) =>
          (a.medicationName || "").localeCompare(b.medicationName || ""),
        ),
      };
    });
  }, [activeOrders, inventory, medicationById, medications, selectedProfiles]);

  const previousDeliveryDetails = useMemo(() => {
    const seen = new Set();
    return activeOrders
      .map((order) => ({
        address: String(order.deliveryAddress || "").trim(),
        instructions: String(order.deliveryInstructions || "").trim(),
      }))
      .filter((delivery) => {
        const key = `${delivery.address.toLowerCase()}|${delivery.instructions.toLowerCase()}`;
        if (!delivery.address || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 8);
  }, [activeOrders]);

  useEffect(() => {
    if (prescriptions.length === 0) return;
    setSelectedPrescriptionIds((prev) => {
      const availableIds = new Set(prescriptions.map((item) => item.id));
      return prev.filter((id) => availableIds.has(id));
    });
  }, [prescriptions]);

  const selectedPrescriptionsForOrder = useMemo(
    () =>
      selectedPrescriptionIds
        .map((id) =>
          prescriptions.find((prescription) => prescription.id === id),
        )
        .filter(Boolean),
    [prescriptions, selectedPrescriptionIds],
  );

  const availablePrescriptionOptions = useMemo(
    () =>
      prescriptions.filter(
        (prescription) => !selectedPrescriptionIds.includes(prescription.id),
      ),
    [prescriptions, selectedPrescriptionIds],
  );

  const orderItemsByProfile = useMemo(() => {
    return selectedProfiles
      .map((profile) => ({
        profile,
        items: orderItems.filter((item) => item.careProfileId === profile.id),
      }))
      .filter((group) => group.items.length > 0);
  }, [orderItems, selectedProfiles]);

  const displayedOrders = useMemo(
    () => (orderHistoryView === "trash" ? trashedOrders : activeOrders),
    [activeOrders, orderHistoryView, trashedOrders],
  );

  const stockActionLines = useMemo(() => {
    if (!stockActionOrder) return [];
    const totalsByLine = getOrderReceiptTotalsByLine(stockActionOrder);
    return (stockActionOrder.items || []).map((item, index) => {
      const key = getOrderItemKey(item, index);
      const orderedQuantity = toNumber(item.quantity);
      const receivedQuantity = toNumber(totalsByLine[key]);
      return {
        key,
        item,
        inventoryItem:
          resolveOrderItemInventoryItem(item, inventoryById, inventory) || null,
        orderedQuantity,
        receivedQuantity,
        remainingQuantity: Math.max(0, orderedQuantity - receivedQuantity),
      };
    });
  }, [inventory, inventoryById, stockActionOrder]);

  const stockActionGroups = useMemo(() => {
    if (!stockActionOrder) return [];

    const profileByIdForOrder = (
      stockActionOrder.profileSummaries || []
    ).reduce((acc, profile) => {
      if (profile.careProfileId) acc[profile.careProfileId] = profile;
      return acc;
    }, {});
    const groupsByTransaction = {
      normal: new Map(),
      senior: new Map(),
    };

    stockActionLines.forEach((line) => {
      const profile =
        profileByIdForOrder[line.item.careProfileId] ||
        profileById.get(line.item.careProfileId) ||
        {};
      const transactionKey = getTransactionGroupKey(
        line.item.transactionType || profile.transactionType,
      );
      const profileId = line.item.careProfileId || "unknown";
      if (!groupsByTransaction[transactionKey].has(profileId)) {
        groupsByTransaction[transactionKey].set(profileId, {
          profileId,
          patientName: line.item.patientName || getProfileLabel(profile),
          lines: [],
        });
      }
      groupsByTransaction[transactionKey].get(profileId).lines.push(line);
    });

    return [
      {
        key: "normal",
        title: "Regular Transaction",
        profiles: Array.from(groupsByTransaction.normal.values()),
      },
      {
        key: "senior",
        title: "Senior Citizen Transaction",
        profiles: Array.from(groupsByTransaction.senior.values()),
      },
    ].filter((group) => group.profiles.length > 0);
  }, [profileById, stockActionLines, stockActionOrder]);

  const stockActionHasQuantity = stockActionLines.some(
    (line) =>
      (stockActionMode === "receive" || line.inventoryItem) &&
      toNumber(stockActionDrafts[line.key], 0) > 0,
  );

  if (!activeCareProfile) {
    return (
      <div className="p-8 text-center text-gray-500">
        Please select a care profile to manage medication ordering.
      </div>
    );
  }

  if (!canReadSelected) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-800">
        You do not have permission to view medication ordering for the selected
        profile.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-green-700">
            {isInventoryView ? "Medication inventory" : "Medication ordering"}
          </p>
          <h2 className="text-2xl font-semibold text-gray-900">
            {isInventoryView ? "Medication Inventory" : "Medication Ordering"}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            {isInventoryView
              ? "Track stock on hand, thresholds, and inventory adjustments."
              : "Prepare individual or group medication orders and manage order history."}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-2xl bg-green-50 p-3">
            <div className="text-xl font-semibold text-green-700">
              {summary.total}
            </div>
            <div className="text-xs text-green-800">Tracked</div>
          </div>
          <div className="rounded-2xl bg-amber-50 p-3">
            <div className="text-xl font-semibold text-amber-700">
              {summary.lowStock}
            </div>
            <div className="text-xs text-amber-800">Low</div>
          </div>
          <div className="rounded-2xl bg-red-50 p-3">
            <div className="text-xl font-semibold text-red-700">
              {summary.outOfStock}
            </div>
            <div className="text-xs text-red-800">Out</div>
          </div>
        </div>
      </div>

      {isOrderingView && (
        <section className="rounded-3xl border border-gray-200 bg-gray-50 p-4">
          <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Medication orders
              </h3>
              <p className="text-sm text-gray-600">
                Start a new order when you are ready. Group orders consolidate
                medicines across accessible profiles into one PDF.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              {!showOrderBuilder && !editingOrder ? (
                <>
                  <button
                    type="button"
                    onClick={() => startNewOrder("single")}
                    className="min-h-11 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                  >
                    New order
                  </button>
                  <button
                    type="button"
                    onClick={() => startNewOrder("group")}
                    className="min-h-11 rounded-xl border border-green-200 bg-white px-4 py-2 text-sm font-semibold text-green-700 hover:bg-green-50"
                  >
                    New group order
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="flex rounded-full bg-white p-1 shadow-sm">
                    <button
                      type="button"
                      onClick={() => handleOrderModeChange("single")}
                      className={`rounded-full px-4 py-2 text-sm font-medium ${
                        orderMode === "single"
                          ? "bg-green-600 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      Single
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOrderModeChange("group")}
                      className={`rounded-full px-4 py-2 text-sm font-medium ${
                        orderMode === "group"
                          ? "bg-green-600 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      Group
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelOrderBuilder}
                    className="min-h-11 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel order
                  </button>
                </div>
              )}
            </div>
          </div>

          {(showOrderBuilder || editingOrder) && orderMode === "group" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {accessibleProfiles.map((profile) => (
                  <label
                    key={profile.id}
                    className="flex min-h-12 items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProfileIds.includes(profile.id)}
                      onChange={(event) =>
                        toggleGroupProfile(profile.id, event.target.checked)
                      }
                      className="h-5 w-5 rounded border-gray-300 text-green-600"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-gray-900">
                        {getProfileLabel(profile)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {hasInventoryPermission(profile.id, "write")
                          ? "Can order and adjust"
                          : "Read only"}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {!groupProfilesConfirmed && (
                <button
                  type="button"
                  onClick={confirmGroupProfiles}
                  className="min-h-11 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                >
                  Confirm profiles and continue
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {isInventoryView && (
        <section className="rounded-3xl border border-gray-200 p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <FaBoxes className="text-green-600" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Stock on hand
                </h3>
                <p className="text-sm text-gray-600">
                  Expand only when you need to change which medicines are
                  tracked.
                </p>
              </div>
            </div>
            {!isInventoryView && (
              <button
                type="button"
                onClick={() => setShowInventoryTracking((prev) => !prev)}
                className="min-h-11 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                {showInventoryTracking
                  ? "Minimize tracking"
                  : "Manage tracking"}
              </button>
            )}
          </div>

          {showInventoryTracking && (
            <div className="space-y-4">
              {canWriteSelected && (
                <div className="mb-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      resetInventoryForm();
                      setShowInventorySetupForm(true);
                    }}
                    className="min-h-11 rounded-xl border border-green-200 px-4 py-2 text-sm font-semibold text-green-700 hover:bg-green-50"
                  >
                    Add custom tracked medicine
                  </button>
                </div>
              )}

              {canWriteSelected && showInventorySetupForm && (
                <form
                  onSubmit={handleSaveInventory}
                  className="mb-6 rounded-2xl bg-gray-50 p-4"
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div>
                      <label
                        htmlFor="inventory-profile"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Patient
                      </label>
                      <select
                        id="inventory-profile"
                        value={inventoryForm.profileId}
                        onChange={(event) =>
                          setInventoryForm((prev) => ({
                            ...prev,
                            profileId: event.target.value,
                            medicationId: "",
                          }))
                        }
                        className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                      >
                        {selectedProfiles.map((profile) => (
                          <option key={profile.id} value={profile.id}>
                            {getProfileLabel(profile)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="inventory-med-template"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Medicine template
                      </label>
                      <select
                        id="inventory-med-template"
                        value={inventoryForm.medicationId}
                        onChange={(event) =>
                          handleMedicationTemplateChange(event.target.value)
                        }
                        className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                      >
                        <option value="">Custom medicine</option>
                        {medications
                          .filter(
                            (med) =>
                              med.careProfileId === inventoryForm.profileId,
                          )
                          .map((med) => (
                            <option key={med.id} value={med.id}>
                              {med.medicationName}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="inventory-name"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Medicine name
                      </label>
                      <input
                        id="inventory-name"
                        value={inventoryForm.medicationName}
                        onChange={(event) =>
                          setInventoryForm((prev) => ({
                            ...prev,
                            medicationName: event.target.value,
                          }))
                        }
                        className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="inventory-quantity"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Stock on hand
                      </label>
                      <input
                        id="inventory-quantity"
                        type="number"
                        min="0"
                        step="0.01"
                        value={inventoryForm.quantityOnHand}
                        onChange={(event) =>
                          setInventoryForm((prev) => ({
                            ...prev,
                            quantityOnHand: event.target.value,
                          }))
                        }
                        className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="inventory-unit"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Unit
                      </label>
                      <input
                        id="inventory-unit"
                        value={inventoryForm.unitLabel}
                        onChange={(event) =>
                          setInventoryForm((prev) => ({
                            ...prev,
                            unitLabel: event.target.value,
                          }))
                        }
                        className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                        placeholder="tablet, capsule, bottle"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="inventory-deduct"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Deduct per dose
                      </label>
                      <input
                        id="inventory-deduct"
                        type="number"
                        min="0"
                        step="0.01"
                        value={inventoryForm.deductQuantityPerAdministration}
                        onChange={(event) =>
                          setInventoryForm((prev) => ({
                            ...prev,
                            deductQuantityPerAdministration: event.target.value,
                          }))
                        }
                        className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="inventory-low-stock"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Low stock threshold
                      </label>
                      <input
                        id="inventory-low-stock"
                        type="number"
                        min="0"
                        step="1"
                        value={inventoryForm.lowStockThreshold}
                        onChange={(event) =>
                          setInventoryForm((prev) => ({
                            ...prev,
                            lowStockThreshold: event.target.value,
                          }))
                        }
                        className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="inventory-strength"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        Strength / form
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          id="inventory-strength"
                          value={inventoryForm.strength}
                          onChange={(event) =>
                            setInventoryForm((prev) => ({
                              ...prev,
                              strength: event.target.value,
                            }))
                          }
                          className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                          placeholder="500 mg"
                        />
                        <input
                          aria-label="Form"
                          value={inventoryForm.form}
                          onChange={(event) =>
                            setInventoryForm((prev) => ({
                              ...prev,
                              form: event.target.value,
                            }))
                          }
                          className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                          placeholder="tablet"
                        />
                      </div>
                    </div>
                  </div>
                  <label
                    htmlFor="inventory-notes"
                    className="mb-1 mt-4 block text-sm font-medium text-gray-700"
                  >
                    Notes
                  </label>
                  <textarea
                    id="inventory-notes"
                    value={inventoryForm.notes}
                    onChange={(event) =>
                      setInventoryForm((prev) => ({
                        ...prev,
                        notes: event.target.value,
                      }))
                    }
                    className="min-h-20 w-full rounded-xl border border-gray-300 px-3 py-2"
                  />
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="submit"
                      disabled={isSavingInventory}
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-60"
                    >
                      <FaSave />{" "}
                      {editingInventoryId ? "Update stock" : "Track medicine"}
                    </button>
                    {editingInventoryId && (
                      <button
                        type="button"
                        onClick={resetInventoryForm}
                        className="min-h-11 rounded-xl border border-gray-300 px-5 py-2 font-semibold text-gray-700 hover:bg-white"
                      >
                        Cancel edit
                      </button>
                    )}
                  </div>
                </form>
              )}

              {isLoadingInventory ? (
                <div className="rounded-2xl bg-gray-50 p-5 text-gray-500">
                  Loading inventory...
                </div>
              ) : (
                <div className="space-y-5">
                  {inventoryByProfile.map(({ profile, items }) => (
                    <div key={profile.id}>
                      <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                        {getProfileLabel(profile)}
                      </h4>
                      {items.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-gray-300 p-5 text-sm text-gray-500">
                          No active medicines or inventory rows yet for this
                          patient.
                        </div>
                      ) : (
                        <>
                          <div className="space-y-3 md:hidden">
                            {items.map((item) => {
                              const status = getStockStatus(item);
                              const adjustment =
                                adjustments[item.id] || emptyAdjustment;
                              const canAdjust = hasInventoryPermission(
                                item.careProfileId,
                                "write",
                              );
                              const detailText = [item.strength, item.form]
                                .filter(Boolean)
                                .join(" | ");
                              const isEditingStock =
                                editingInventoryId === item.id;
                              const showAdjustmentControls =
                                item.isInventoryTracked &&
                                canAdjust &&
                                isEditingStock;
                              return (
                                <article
                                  key={item.rowId}
                                  className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <h5 className="break-words text-sm font-semibold text-gray-900">
                                        {item.medicationName}
                                      </h5>
                                      {detailText && (
                                        <p className="mt-0.5 text-xs text-gray-500">
                                          {detailText}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      disabled={!canAdjust}
                                      aria-pressed={item.isInventoryTracked}
                                      aria-label={`Turn ${
                                        item.isInventoryTracked ? "off" : "on"
                                      } inventory tracking for ${
                                        item.medicationName
                                      }`}
                                      onClick={() =>
                                        handleToggleInventoryTracking(
                                          item,
                                          !item.isInventoryTracked,
                                        )
                                      }
                                      className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium shadow-sm transition disabled:opacity-50 ${
                                        item.isInventoryTracked
                                          ? "border-green-200 bg-white text-green-700 hover:bg-green-50"
                                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                      }`}
                                    >
                                      <span
                                        className={`relative h-4 w-7 rounded-full transition ${
                                          item.isInventoryTracked
                                            ? "bg-green-500"
                                            : "bg-slate-300"
                                        }`}
                                      >
                                        <span
                                          className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition ${
                                            item.isInventoryTracked
                                              ? "left-3.5"
                                              : "left-0.5"
                                          }`}
                                        />
                                      </span>
                                      {item.isInventoryTracked ? "On" : "Off"}
                                    </button>
                                  </div>

                                  {item.isInventoryTracked && (
                                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                      <div>
                                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                          Stock
                                        </p>
                                        <p className="mt-1 text-gray-700">
                                          <span className="font-semibold text-gray-900">
                                            {toNumber(item.quantityOnHand)}
                                          </span>{" "}
                                          <span className="text-gray-500">
                                            {item.unitLabel || "unit"}
                                          </span>
                                        </p>
                                        <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                                          <span
                                            className={`h-1.5 w-1.5 rounded-full ${getStockStatusDotClasses(
                                              status.label,
                                            )}`}
                                          />
                                          {status.label}
                                        </span>
                                      </div>
                                      {isEditingStock && (
                                        <>
                                          <div>
                                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                              Deduct / dose
                                            </p>
                                            <p className="mt-1 text-gray-700">
                                              {toNumber(
                                                item.deductQuantityPerAdministration,
                                                1,
                                              )}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                              Low threshold
                                            </p>
                                            <input
                                              key={`${item.medicationInventoryId}-${item.lowStockThreshold}`}
                                              aria-label={`Low stock threshold for ${item.medicationName}`}
                                              type="number"
                                              min="0"
                                              step="1"
                                              defaultValue={toNumber(
                                                item.lowStockThreshold,
                                              )}
                                              onBlur={(event) =>
                                                handleLowStockThresholdChange(
                                                  item,
                                                  event.target.value,
                                                )
                                              }
                                              onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                  event.currentTarget.blur();
                                                }
                                              }}
                                              disabled={!canAdjust}
                                              className="mt-1 min-h-10 w-full rounded-xl border border-gray-300 px-3 py-2 disabled:bg-gray-100"
                                            />
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}

                                  <div className="mt-4">
                                    {item.isInventoryTracked && canAdjust ? (
                                      <button
                                        type="button"
                                        data-inventory-edit-trigger={item.id}
                                        onClick={() =>
                                          handleEditInventory(item)
                                        }
                                        className="min-h-10 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                      >
                                        Edit stock
                                      </button>
                                    ) : (
                                      <p className="text-sm text-gray-400">
                                        Enable tracking first
                                      </p>
                                    )}
                                  </div>

                                  {showAdjustmentControls && (
                                    <div
                                      data-stock-edit-panel={item.id}
                                      className="mt-4 rounded-2xl bg-gray-50 p-3"
                                    >
                                      <div className="grid grid-cols-1 gap-2">
                                        <select
                                          aria-label={`Adjustment type for ${item.medicationName}`}
                                          value={adjustment.mode}
                                          onChange={(event) =>
                                            updateAdjustmentDraft(item.id, {
                                              mode: event.target.value,
                                            })
                                          }
                                          className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                                        >
                                          <option value="set">
                                            Set exact count
                                          </option>
                                          <option value="add">Add stock</option>
                                          <option value="subtract">
                                            Subtract stock
                                          </option>
                                        </select>
                                        <input
                                          aria-label={`Adjustment quantity for ${item.medicationName}`}
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={adjustment.quantity}
                                          onChange={(event) =>
                                            updateAdjustmentDraft(item.id, {
                                              quantity: event.target.value,
                                            })
                                          }
                                          className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                                          placeholder="Quantity"
                                        />
                                        <input
                                          aria-label={`Adjustment reason for ${item.medicationName}`}
                                          value={adjustment.reason}
                                          onChange={(event) =>
                                            updateAdjustmentDraft(item.id, {
                                              reason: event.target.value,
                                            })
                                          }
                                          className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                                          placeholder="Reason, e.g. missed administration log"
                                        />
                                      </div>
                                      <div className="mt-3 grid grid-cols-2 gap-2">
                                        <button
                                          type="button"
                                          onClick={resetInventoryForm}
                                          className="min-h-11 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleApplyAdjustment(item)
                                          }
                                          className="min-h-11 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                                        >
                                          Adjust stock
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </article>
                              );
                            })}
                          </div>

                          <div className="hidden overflow-x-auto rounded-2xl border border-gray-200 md:block">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                <tr>
                                  <th className="px-3 py-3">Tracking status</th>
                                  <th className="px-3 py-3">Medicine</th>
                                  <th className="px-3 py-3">Stock on hand</th>
                                  <th className="px-3 py-3">Deduct / dose</th>
                                  <th className="px-3 py-3">Low threshold</th>
                                  <th className="px-3 py-3">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {items.map((item) => {
                                  const status = getStockStatus(item);
                                  const adjustment =
                                    adjustments[item.id] || emptyAdjustment;
                                  const canAdjust = hasInventoryPermission(
                                    item.careProfileId,
                                    "write",
                                  );
                                  const showAdjustmentControls =
                                    item.isInventoryTracked &&
                                    canAdjust &&
                                    editingInventoryId === item.id;
                                  return (
                                    <React.Fragment key={item.rowId}>
                                      <tr>
                                        <td className="px-3 py-3 align-top">
                                          <button
                                            type="button"
                                            disabled={!canAdjust}
                                            aria-pressed={
                                              item.isInventoryTracked
                                            }
                                            aria-label={`Turn ${
                                              item.isInventoryTracked
                                                ? "off"
                                                : "on"
                                            } inventory tracking for ${
                                              item.medicationName
                                            }`}
                                            onClick={() =>
                                              handleToggleInventoryTracking(
                                                item,
                                                !item.isInventoryTracked,
                                              )
                                            }
                                            className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium shadow-sm transition disabled:opacity-50 ${
                                              item.isInventoryTracked
                                                ? "border-green-200 bg-white text-green-700 hover:bg-green-50"
                                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                            }`}
                                          >
                                            <span
                                              className={`relative h-4 w-7 rounded-full transition ${
                                                item.isInventoryTracked
                                                  ? "bg-green-500"
                                                  : "bg-slate-300"
                                              }`}
                                            >
                                              <span
                                                className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition ${
                                                  item.isInventoryTracked
                                                    ? "left-3.5"
                                                    : "left-0.5"
                                                }`}
                                              />
                                            </span>
                                            {item.isInventoryTracked
                                              ? "On"
                                              : "Off"}
                                          </button>
                                        </td>
                                        <td className="min-w-56 px-3 py-3 align-top">
                                          <div className="font-semibold text-gray-900">
                                            {item.medicationName}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {[item.strength, item.form]
                                              .filter(Boolean)
                                              .join(" | ") ||
                                              "No strength/form recorded"}
                                          </div>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-3 align-top">
                                          {item.isInventoryTracked ? (
                                            <>
                                              <span className="font-semibold text-gray-900">
                                                {toNumber(item.quantityOnHand)}
                                              </span>{" "}
                                              <span className="text-gray-500">
                                                {item.unitLabel || "unit"}
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-gray-400">
                                              -
                                            </span>
                                          )}
                                          {item.isInventoryTracked && (
                                            <div className="mt-2">
                                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                                                <span
                                                  className={`h-1.5 w-1.5 rounded-full ${getStockStatusDotClasses(
                                                    status.label,
                                                  )}`}
                                                />
                                                {status.label}
                                              </span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-3 align-top">
                                          {item.isInventoryTracked
                                            ? toNumber(
                                                item.deductQuantityPerAdministration,
                                                1,
                                              )
                                            : "-"}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-3 align-top">
                                          {item.isInventoryTracked ? (
                                            <input
                                              key={`${item.medicationInventoryId}-${item.lowStockThreshold}`}
                                              aria-label={`Low stock threshold for ${item.medicationName}`}
                                              type="number"
                                              min="0"
                                              step="1"
                                              defaultValue={toNumber(
                                                item.lowStockThreshold,
                                              )}
                                              onBlur={(event) =>
                                                handleLowStockThresholdChange(
                                                  item,
                                                  event.target.value,
                                                )
                                              }
                                              onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                  event.currentTarget.blur();
                                                }
                                              }}
                                              disabled={!canAdjust}
                                              className="min-h-10 w-24 rounded-xl border border-gray-300 px-3 py-2 disabled:bg-gray-100"
                                            />
                                          ) : (
                                            "-"
                                          )}
                                        </td>
                                        <td className="px-3 py-3 align-top">
                                          {item.isInventoryTracked &&
                                          canAdjust ? (
                                            <button
                                              type="button"
                                              data-inventory-edit-trigger={
                                                item.id
                                              }
                                              onClick={() =>
                                                handleEditInventory(item)
                                              }
                                              className="min-h-10 rounded-xl border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                            >
                                              Edit
                                            </button>
                                          ) : (
                                            <span className="text-gray-400">
                                              Enable tracking first
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                      {showAdjustmentControls && (
                                        <tr>
                                          <td
                                            colSpan={6}
                                            data-stock-edit-panel={item.id}
                                            className="bg-gray-50 px-3 py-3"
                                          >
                                            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                                              <select
                                                aria-label={`Adjustment type for ${item.medicationName}`}
                                                value={adjustment.mode}
                                                onChange={(event) =>
                                                  updateAdjustmentDraft(
                                                    item.id,
                                                    {
                                                      mode: event.target.value,
                                                    },
                                                  )
                                                }
                                                className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                                              >
                                                <option value="set">
                                                  Set exact count
                                                </option>
                                                <option value="add">
                                                  Add stock
                                                </option>
                                                <option value="subtract">
                                                  Subtract stock
                                                </option>
                                              </select>
                                              <input
                                                aria-label={`Adjustment quantity for ${item.medicationName}`}
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={adjustment.quantity}
                                                onChange={(event) =>
                                                  updateAdjustmentDraft(
                                                    item.id,
                                                    {
                                                      quantity:
                                                        event.target.value,
                                                    },
                                                  )
                                                }
                                                className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                                                placeholder="Quantity"
                                              />
                                              <input
                                                aria-label={`Adjustment reason for ${item.medicationName}`}
                                                value={adjustment.reason}
                                                onChange={(event) =>
                                                  updateAdjustmentDraft(
                                                    item.id,
                                                    {
                                                      reason:
                                                        event.target.value,
                                                    },
                                                  )
                                                }
                                                className="min-h-11 rounded-xl border border-gray-300 px-3 py-2 md:col-span-2"
                                                placeholder="Reason, e.g. missed administration log"
                                              />
                                            </div>
                                            <div className="mt-3 flex justify-end gap-2">
                                              <button
                                                type="button"
                                                onClick={resetInventoryForm}
                                                className="min-h-11 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
                                              >
                                                Cancel
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  handleApplyAdjustment(item)
                                                }
                                                className="min-h-11 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                                              >
                                                Adjust stock
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {isOrderingView &&
        (showOrderBuilder || editingOrder) &&
        (orderMode !== "group" || groupProfilesConfirmed) && (
          <section
            id="medication-order-builder"
            className="rounded-3xl border border-gray-200 p-5"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingOrder ? "Edit order" : "Order builder"}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Add medicines by patient. Group mode keeps each line
                  attributed while producing one consolidated report.
                </p>
              </div>
              {editingOrder && (
                <button
                  type="button"
                  onClick={handleCancelOrderEdit}
                  className="min-h-11 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel edit
                </button>
              )}
            </div>
            {editingOrder && (
              <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                You are editing an order from{" "}
                {formatDateTime(editingOrder.finalizedAt)}. Updating it will
                replace the saved order history and download a new PDF.
              </div>
            )}

            <div className="mt-5 rounded-2xl bg-gray-50 p-4">
              <h4 className="font-semibold text-gray-900">Delivery details</h4>
              <p className="mt-1 text-sm text-gray-600">
                Add the address and delivery instructions to include them in the
                saved order, PDF, and text notes.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                <div>
                  <label
                    htmlFor="delivery-address"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Address
                  </label>
                  <textarea
                    id="delivery-address"
                    value={deliveryAddress}
                    onChange={(event) => setDeliveryAddress(event.target.value)}
                    className="min-h-24 w-full rounded-xl border border-gray-300 px-3 py-2"
                    placeholder="Delivery address"
                  />
                  {previousDeliveryDetails.length > 0 && (
                    <>
                      <label
                        htmlFor="previous-delivery-details"
                        className="mt-2 block text-xs font-medium text-gray-500"
                      >
                        Use previous delivery details
                      </label>
                      <select
                        id="previous-delivery-details"
                        value=""
                        onChange={(event) => {
                          if (event.target.value) {
                            const previous =
                              previousDeliveryDetails[
                                Number(event.target.value)
                              ];
                            if (previous) {
                              setDeliveryAddress(previous.address);
                              setDeliveryInstructions(previous.instructions);
                            }
                          }
                        }}
                        className="mt-1 min-h-10 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="">
                          Select previous address and instructions
                        </option>
                        {previousDeliveryDetails.map((delivery, index) => (
                          <option
                            key={`${delivery.address}-${delivery.instructions}`}
                            value={index}
                          >
                            {delivery.address}
                            {delivery.instructions
                              ? ` - ${delivery.instructions}`
                              : ""}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="delivery-instructions"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Instructions when delivering
                  </label>
                  <textarea
                    id="delivery-instructions"
                    value={deliveryInstructions}
                    onChange={(event) =>
                      setDeliveryInstructions(event.target.value)
                    }
                    className="min-h-24 w-full rounded-xl border border-gray-300 px-3 py-2"
                    placeholder="Example: Please call before delivery, leave with guard, etc."
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-gray-200 p-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Attach prescriptions
                  </h4>
                  <p className="text-sm text-gray-600">
                    Select prescriptions to list on the generated order PDF.
                  </p>
                </div>
                <div className="text-sm font-semibold text-gray-700">
                  {selectedPrescriptionIds.length} selected
                </div>
              </div>

              {prescriptions.length === 0 ? (
                <div className="mt-3 rounded-2xl border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                  No prescriptions available for the selected profile
                  {selectedProfiles.length === 1 ? "" : "s"}.
                </div>
              ) : (
                <div className="mt-3 space-y-3">
                  <div>
                    <label
                      htmlFor="attached-prescription-selector"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      Add prescription
                    </label>
                    <select
                      id="attached-prescription-selector"
                      value=""
                      onChange={(event) => {
                        if (!event.target.value) return;
                        setSelectedPrescriptionIds((prev) => [
                          ...new Set([...prev, event.target.value]),
                        ]);
                      }}
                      className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                    >
                      <option value="">
                        {availablePrescriptionOptions.length === 0
                          ? "All available prescriptions attached"
                          : "Select a prescription to attach"}
                      </option>
                      {availablePrescriptionOptions.map((prescription) => {
                        const profile = profileById.get(
                          prescription.careProfileId,
                        );
                        return (
                          <option key={prescription.id} value={prescription.id}>
                            {getProfileLabel(profile)} -{" "}
                            {getPrescriptionName(prescription)} -{" "}
                            {formatDateOnly(getPrescriptionDate(prescription))}
                            {prescription.physicianName
                              ? ` - ${prescription.physicianName}`
                              : ""}
                            {prescription.fileName
                              ? ` - ${prescription.fileName}`
                              : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {selectedPrescriptionsForOrder.length > 0 && (
                    <div className="rounded-2xl bg-gray-50 p-3">
                      <h5 className="text-sm font-semibold text-gray-900">
                        Attached prescriptions
                      </h5>
                      <div className="mt-2 space-y-2">
                        {selectedPrescriptionsForOrder.map((prescription) => {
                          const profile = profileById.get(
                            prescription.careProfileId,
                          );
                          return (
                            <div
                              key={prescription.id}
                              className="flex flex-col gap-2 rounded-xl bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="text-sm">
                                <div className="font-semibold text-gray-900">
                                  {getProfileLabel(profile)} -{" "}
                                  {getPrescriptionName(prescription)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatDateOnly(
                                    getPrescriptionDate(prescription),
                                  )}
                                  {prescription.physicianName
                                    ? ` | ${prescription.physicianName}`
                                    : ""}
                                  {prescription.fileName
                                    ? ` | ${prescription.fileName}`
                                    : ""}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedPrescriptionIds((prev) =>
                                    prev.filter((id) => id !== prescription.id),
                                  )
                                }
                                className="min-h-10 rounded-xl px-3 text-sm font-semibold text-red-600 hover:bg-red-50"
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {!canWriteSelected ? (
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                You can view inventory, but you need write access for all
                selected profiles to build an order.
              </div>
            ) : orderMode === "group" && !groupProfilesConfirmed ? (
              <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                Select the profiles for this group order above, then click{" "}
                <strong>Confirm profiles and continue</strong> to open their
                medication lists.
              </div>
            ) : (
              <div className="mt-5 space-y-6">
                <div className="space-y-5">
                  {orderRowsByProfile.map(({ profile, items }) => (
                    <div key={profile.id} className="rounded-2xl border p-4">
                      <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {getProfileLabel(profile)}
                          </h4>
                          <p className="text-sm text-gray-500">
                            Enter a quantity to include a medicine in this
                            order. Leave it at 0 to skip.
                          </p>
                        </div>
                        <div className="text-sm text-gray-500">
                          {items.length} available medicine
                          {items.length === 1 ? "" : "s"}
                        </div>
                      </div>

                      <div className="mb-4 grid grid-cols-1 gap-3 rounded-2xl bg-gray-50 p-3 md:grid-cols-2">
                        <div>
                          <label
                            htmlFor={`transaction-type-${profile.id}`}
                            className="mb-1 block text-sm font-medium text-gray-700"
                          >
                            Transaction type
                          </label>
                          <select
                            id={`transaction-type-${profile.id}`}
                            value={getProfileTransactionType(
                              profile.id,
                              transactionTypesByProfile,
                            )}
                            onChange={(event) =>
                              setTransactionTypesByProfile((prev) => ({
                                ...prev,
                                [profile.id]: event.target.value,
                              }))
                            }
                            className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                          >
                            <option value="normal">Regular transaction</option>
                            <option value="senior">
                              Senior citizen transaction
                            </option>
                          </select>
                        </div>
                        <div>
                          <label
                            htmlFor={`senior-id-${profile.id}`}
                            className="mb-1 block text-sm font-medium text-gray-700"
                          >
                            Senior ID
                          </label>
                          <input
                            id={`senior-id-${profile.id}`}
                            value={
                              seniorIds[profile.id] || getSeniorId(profile)
                            }
                            onChange={(event) =>
                              setSeniorIds((prev) => ({
                                ...prev,
                                [profile.id]: event.target.value,
                              }))
                            }
                            className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      {items.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-gray-300 p-5 text-sm text-gray-500">
                          No active medicines or inventory rows yet for this
                          patient. Add a new medicine line below if it still
                          needs to be ordered.
                        </div>
                      ) : (
                        <>
                          <div className="space-y-3 md:hidden">
                            {items.map((item) => {
                              const status = getStockStatus(item);
                              const lastOrderedQuantity =
                                getLastOrderedQuantity(item, orders);
                              const thresholdSuggested =
                                getThresholdSuggestedOrderQuantity(item);
                              const orderItem =
                                getOrderItemForMedicineRow(item);
                              const orderQuantity = toNumber(
                                orderItem?.quantity,
                              );
                              const genericName = getOrderSelectionGenericName(
                                item,
                                orderItem,
                              );

                              return (
                                <article
                                  key={item.rowId}
                                  className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm"
                                >
                                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div>
                                      <h5 className="text-base font-semibold text-gray-900">
                                        {item.medicationName}
                                      </h5>
                                      <p className="mt-1 text-sm text-gray-500">
                                        {[item.strength, item.form]
                                          .filter(Boolean)
                                          .join(" | ") || "No strength/form"}
                                      </p>
                                    </div>
                                    <span
                                      className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${status.classes}`}
                                    >
                                      {status.label}
                                    </span>
                                  </div>

                                  <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-600 sm:grid-cols-2">
                                    <div>
                                      <span className="font-medium text-gray-700">
                                        Stock:
                                      </span>{" "}
                                      {item.isInventoryTracked ? (
                                        <>
                                          {toNumber(item.quantityOnHand)}{" "}
                                          {item.unitLabel || "unit"}
                                        </>
                                      ) : (
                                        "Not set up"
                                      )}
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">
                                        Suggested:
                                      </span>{" "}
                                      {lastOrderedQuantity > 0 ||
                                      thresholdSuggested > 0 ? (
                                        <span className="inline-flex flex-wrap gap-2">
                                          {lastOrderedQuantity > 0 && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                upsertMedicineOrderItem(
                                                  item,
                                                  lastOrderedQuantity,
                                                )
                                              }
                                              className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-100"
                                            >
                                              {`Last ordered ${lastOrderedQuantity}`}
                                            </button>
                                          )}
                                          {thresholdSuggested > 0 && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                upsertMedicineOrderItem(
                                                  item,
                                                  thresholdSuggested,
                                                )
                                              }
                                              className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                            >
                                              Suggested {thresholdSuggested}
                                            </button>
                                          )}
                                        </span>
                                      ) : (
                                        "None"
                                      )}
                                    </div>
                                  </div>

                                  <div className="mt-4">
                                    <label
                                      htmlFor={`order-quantity-${item.rowId}`}
                                      className="mb-2 block text-sm font-medium text-gray-700"
                                    >
                                      Order quantity
                                    </label>
                                    <div className="flex w-full max-w-xs items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          upsertMedicineOrderItem(
                                            item,
                                            orderQuantity - 1,
                                          )
                                        }
                                        className="flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50"
                                        aria-label={`Decrease order quantity for ${item.medicationName}`}
                                      >
                                        <FaMinus />
                                      </button>
                                      <input
                                        id={`order-quantity-${item.rowId}`}
                                        aria-label={`Order quantity for ${item.medicationName}`}
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={orderQuantity}
                                        onChange={(event) =>
                                          upsertMedicineOrderItem(
                                            item,
                                            event.target.value,
                                          )
                                        }
                                        className="min-h-11 min-w-0 flex-1 rounded-xl border border-gray-300 px-3 py-2 text-center text-base font-semibold"
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          upsertMedicineOrderItem(
                                            item,
                                            orderQuantity + 1,
                                          )
                                        }
                                        className="flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50"
                                        aria-label={`Increase order quantity for ${item.medicationName}`}
                                      >
                                        <FaPlus />
                                      </button>
                                    </div>
                                  </div>

                                  <div className="mt-3">
                                    <label className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-gray-50 px-3 text-sm font-semibold text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={
                                          !!orderItem?.useGenericAlternative
                                        }
                                        onChange={() =>
                                          orderItem &&
                                          toggleOrderItemGeneric(
                                            orderItem.lineId,
                                          )
                                        }
                                        disabled={!orderItem || !genericName}
                                        className="h-4 w-4 rounded border-gray-300"
                                      />
                                      {genericName
                                        ? `Order generic: ${genericName}`
                                        : "No generic alternative recorded"}
                                    </label>
                                    {!orderItem && genericName && (
                                      <p className="mt-1 text-xs text-gray-500">
                                        Set a quantity first to enable this
                                        option.
                                      </p>
                                    )}
                                  </div>

                                  <div className="mt-3">
                                    <label
                                      htmlFor={`order-notes-${item.rowId}`}
                                      className="mb-2 block text-sm font-medium text-gray-700"
                                    >
                                      Notes
                                    </label>
                                    <input
                                      id={`order-notes-${item.rowId}`}
                                      aria-label={`Order notes for ${item.medicationName}`}
                                      value={orderItem?.notes || ""}
                                      onChange={(event) =>
                                        orderItem &&
                                        updateOrderItemNotes(
                                          orderItem.lineId,
                                          event.target.value,
                                        )
                                      }
                                      className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2"
                                      placeholder={
                                        orderItem
                                          ? "Optional note"
                                          : "Set quantity first"
                                      }
                                      disabled={!orderItem}
                                    />
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                          <div className="hidden overflow-x-auto md:block">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                <tr>
                                  <th className="px-3 py-3">Medicine</th>
                                  <th className="px-3 py-3">Stock</th>
                                  <th className="px-3 py-3">Status</th>
                                  <th className="px-3 py-3">Suggested</th>
                                  <th className="px-3 py-3">Order qty</th>
                                  <th className="px-3 py-3">Generic</th>
                                  <th className="px-3 py-3">Notes</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {items.map((item) => {
                                  const status = getStockStatus(item);
                                  const lastOrderedQuantity =
                                    getLastOrderedQuantity(item, orders);
                                  const thresholdSuggested =
                                    getThresholdSuggestedOrderQuantity(item);
                                  const orderItem =
                                    getOrderItemForMedicineRow(item);
                                  const orderQuantity = toNumber(
                                    orderItem?.quantity,
                                  );
                                  const genericName =
                                    getOrderSelectionGenericName(
                                      item,
                                      orderItem,
                                    );
                                  return (
                                    <tr key={item.rowId}>
                                      <td className="min-w-56 px-3 py-3 align-top">
                                        <div className="font-semibold text-gray-900">
                                          {item.medicationName}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {[item.strength, item.form]
                                            .filter(Boolean)
                                            .join(" | ") || "No strength/form"}
                                        </div>
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-3 align-top">
                                        {item.isInventoryTracked ? (
                                          <>
                                            <span className="font-semibold text-gray-900">
                                              {toNumber(item.quantityOnHand)}
                                            </span>{" "}
                                            <span className="text-gray-500">
                                              {item.unitLabel || "unit"}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="text-gray-400">
                                            Not set up
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-3 align-top">
                                        <span
                                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${status.classes}`}
                                        >
                                          {status.label}
                                        </span>
                                      </td>
                                      <td className="whitespace-nowrap px-3 py-3 align-top">
                                        {lastOrderedQuantity > 0 ||
                                        thresholdSuggested > 0 ? (
                                          <div className="flex flex-wrap gap-2">
                                            {lastOrderedQuantity > 0 && (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  upsertMedicineOrderItem(
                                                    item,
                                                    lastOrderedQuantity,
                                                  )
                                                }
                                                className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-100"
                                              >
                                                {`Last ordered ${lastOrderedQuantity}`}
                                              </button>
                                            )}
                                            {thresholdSuggested > 0 && (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  upsertMedicineOrderItem(
                                                    item,
                                                    thresholdSuggested,
                                                  )
                                                }
                                                className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                              >
                                                Suggested {thresholdSuggested}
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-gray-400">
                                            None
                                          </span>
                                        )}
                                      </td>
                                      <td className="min-w-40 px-3 py-3 align-top">
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={() =>
                                              upsertMedicineOrderItem(
                                                item,
                                                orderQuantity - 1,
                                              )
                                            }
                                            className="min-h-10 rounded-xl border border-gray-300 px-3 text-gray-700 hover:bg-gray-50"
                                            aria-label={`Decrease order quantity for ${item.medicationName}`}
                                          >
                                            <FaMinus />
                                          </button>
                                          <input
                                            aria-label={`Order quantity for ${item.medicationName}`}
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={orderQuantity}
                                            onChange={(event) =>
                                              upsertMedicineOrderItem(
                                                item,
                                                event.target.value,
                                              )
                                            }
                                            className="min-h-10 w-20 rounded-xl border border-gray-300 px-3 py-2 text-center"
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              upsertMedicineOrderItem(
                                                item,
                                                orderQuantity + 1,
                                              )
                                            }
                                            className="min-h-10 rounded-xl border border-gray-300 px-3 text-gray-700 hover:bg-gray-50"
                                            aria-label={`Increase order quantity for ${item.medicationName}`}
                                          >
                                            <FaPlus />
                                          </button>
                                        </div>
                                      </td>
                                      <td className="min-w-56 px-3 py-3 align-top">
                                        <label className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-gray-50 px-3 text-xs font-semibold text-gray-700">
                                          <input
                                            type="checkbox"
                                            checked={
                                              !!orderItem?.useGenericAlternative
                                            }
                                            onChange={() =>
                                              orderItem &&
                                              toggleOrderItemGeneric(
                                                orderItem.lineId,
                                              )
                                            }
                                            disabled={
                                              !orderItem || !genericName
                                            }
                                            className="h-4 w-4 rounded border-gray-300"
                                          />
                                          {genericName || "No generic"}
                                        </label>
                                        {!orderItem && genericName && (
                                          <div className="mt-1 text-xs text-gray-500">
                                            Set quantity first.
                                          </div>
                                        )}
                                      </td>
                                      <td className="min-w-52 px-3 py-3 align-top">
                                        <input
                                          aria-label={`Order notes for ${item.medicationName}`}
                                          value={orderItem?.notes || ""}
                                          onChange={(event) =>
                                            orderItem &&
                                            updateOrderItemNotes(
                                              orderItem.lineId,
                                              event.target.value,
                                            )
                                          }
                                          className="min-h-10 w-full rounded-xl border border-gray-300 px-3 py-2"
                                          placeholder={
                                            orderItem
                                              ? "Optional note"
                                              : "Set quantity first"
                                          }
                                          disabled={!orderItem}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="rounded-2xl bg-gray-50 p-4">
                  <h4 className="font-semibold text-gray-900">
                    New medicine line
                  </h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Use this when a medicine is not yet in inventory but should
                    still appear on the order report.
                  </p>
                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
                    <select
                      aria-label="New medicine patient"
                      value={orderDraft.profileId}
                      onChange={(event) =>
                        setOrderDraft((prev) => ({
                          ...prev,
                          profileId: event.target.value,
                          inventoryId: "",
                        }))
                      }
                      className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                    >
                      {selectedProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {getProfileLabel(profile)}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label="New medicine name"
                      value={orderDraft.customMedicationName}
                      onChange={(event) =>
                        setOrderDraft((prev) => ({
                          ...prev,
                          customMedicationName: event.target.value,
                        }))
                      }
                      className="min-h-11 rounded-xl border border-gray-300 px-3 py-2 xl:col-span-2"
                      placeholder="Medicine name"
                    />
                    <input
                      aria-label="New medicine quantity"
                      type="number"
                      min="1"
                      step="1"
                      value={orderDraft.quantity}
                      onChange={(event) =>
                        setOrderDraft((prev) => ({
                          ...prev,
                          quantity: event.target.value,
                        }))
                      }
                      className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                    />
                    <input
                      aria-label="New medicine unit"
                      value={orderDraft.unitLabel}
                      onChange={(event) =>
                        setOrderDraft((prev) => ({
                          ...prev,
                          unitLabel: event.target.value,
                        }))
                      }
                      className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                      placeholder="Unit"
                    />
                    <button
                      type="button"
                      onClick={addOrderItem}
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700"
                    >
                      <FaPlus /> Add
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                    <input
                      aria-label="New medicine strength"
                      value={orderDraft.customStrength}
                      onChange={(event) =>
                        setOrderDraft((prev) => ({
                          ...prev,
                          customStrength: event.target.value,
                        }))
                      }
                      className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                      placeholder="Strength"
                    />
                    <input
                      aria-label="New medicine form"
                      value={orderDraft.customForm}
                      onChange={(event) =>
                        setOrderDraft((prev) => ({
                          ...prev,
                          customForm: event.target.value,
                        }))
                      }
                      className="min-h-11 rounded-xl border border-gray-300 px-3 py-2"
                      placeholder="Form"
                    />
                    <input
                      aria-label="New medicine notes"
                      value={orderDraft.notes}
                      onChange={(event) =>
                        setOrderDraft((prev) => ({
                          ...prev,
                          notes: event.target.value,
                        }))
                      }
                      className="min-h-11 rounded-xl border border-gray-300 px-3 py-2 md:col-span-2"
                      placeholder="Notes"
                    />
                  </div>
                </div>
              </div>
            )}

            {!showOrderCart && (
              <div className="mt-6 flex flex-col gap-2 rounded-2xl bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Ready to review?
                  </h4>
                  <p className="text-sm text-gray-600">
                    Proceed when medicine quantities are selected.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleProceedToOrderCart}
                  className="inline-flex min-h-11 items-center justify-center rounded-xl bg-green-600 px-5 py-2 font-semibold text-white hover:bg-green-700"
                >
                  {orderItems.length === 0
                    ? "Add quantities to proceed"
                    : `Proceed with ${orderItems.length} item${
                        orderItems.length === 1 ? "" : "s"
                      }`}
                </button>
              </div>
            )}

            {showOrderCart && (
              <>
                <div id="medication-order-cart" className="mt-6 space-y-4">
                  <div className="flex flex-col gap-1 rounded-2xl bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Order list
                      </h4>
                      <p className="text-sm text-gray-600">
                        Medicines with quantity greater than 0 will be saved and
                        added to the PDF.
                      </p>
                      {orderItems.some((item) => item.isNewMedication) && (
                        <p className="mt-1 text-xs text-gray-500">
                          * New medicine added from this order.
                        </p>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-gray-700">
                      {orderItems.length} selected item
                      {orderItems.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  {orderItemsByProfile.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-5 text-sm text-amber-800">
                      No medicines added to this order yet. Enter a quantity in
                      the medicine table above or add a new medicine line before
                      finalizing.
                    </div>
                  ) : (
                    orderItemsByProfile.map(({ profile, items }) => (
                      <div key={profile.id} className="rounded-2xl border p-4">
                        <h4 className="font-semibold text-gray-900">
                          {getProfileLabel(profile)}
                        </h4>
                        <div className="mt-3 space-y-2">
                          {items.map((item) => (
                            <div
                              key={item.lineId}
                              className="flex flex-col gap-3 rounded-2xl bg-gray-50 p-3 md:flex-row md:items-center md:justify-between"
                            >
                              <div>
                                <div className="font-semibold text-gray-900">
                                  {getOrderMedicationName(item)}
                                  {item.isNewMedication ? "*" : ""}
                                </div>
                                {getGenericName(item) && (
                                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                    <label className="inline-flex min-h-9 items-center gap-2 rounded-xl bg-white px-3 text-xs font-semibold text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={!!item.useGenericAlternative}
                                        onChange={() =>
                                          toggleOrderItemGeneric(item.lineId)
                                        }
                                        className="h-4 w-4 rounded border-gray-300"
                                      />
                                      Order generic: {getGenericName(item)}
                                    </label>
                                    {item.useGenericAlternative && (
                                      <label className="inline-flex min-h-9 items-center gap-2 rounded-xl bg-white px-3 text-xs font-semibold text-gray-700">
                                        <input
                                          type="checkbox"
                                          checked={
                                            !!item.addGenericToMedications
                                          }
                                          onChange={() =>
                                            toggleAddGenericToMedications(
                                              item.lineId,
                                            )
                                          }
                                          className="h-4 w-4 rounded border-gray-300"
                                        />
                                        Add to Active & PRN medications
                                      </label>
                                    )}
                                  </div>
                                )}
                                <div className="text-sm text-gray-600">
                                  {[item.strength, item.form]
                                    .filter(Boolean)
                                    .join(" | ")}
                                </div>
                              </div>
                              <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:flex-nowrap">
                                <div className="flex w-full items-center gap-2 md:w-auto">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateOrderItemQuantity(item.lineId, -1)
                                    }
                                    className="flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-gray-300"
                                  >
                                    <FaMinus />
                                  </button>
                                  <span className="min-w-0 flex-1 text-center font-semibold">
                                    {item.quantity} {item.unitLabel}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateOrderItemQuantity(item.lineId, 1)
                                    }
                                    className="flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-gray-300"
                                  >
                                    <FaPlus />
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeOrderItem(item.lineId)}
                                  className="min-h-11 w-full rounded-xl px-3 text-sm font-semibold text-red-600 hover:bg-red-50 md:w-auto"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    onClick={handleFinalizeOrder}
                    disabled={isFinalizingOrder}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-2 font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
                  >
                    <FaFilePdf />{" "}
                    {orderItems.length === 0
                      ? "Add quantities to finalize"
                      : editingOrder
                        ? "Preview updated order"
                        : "Preview PDF"}
                  </button>
                </div>
              </>
            )}
          </section>
        )}

      {isOrderingView && (
        <section className="rounded-3xl border border-gray-200 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Order history
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Orders stay separate from inventory until stock is received.
              </p>
            </div>
            <div className="inline-flex rounded-2xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setOrderHistoryView("active")}
                className={`min-h-10 rounded-xl px-4 text-sm font-semibold ${
                  orderHistoryView === "active"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Active ({activeOrders.length})
              </button>
              <button
                type="button"
                onClick={() => setOrderHistoryView("trash")}
                className={`min-h-10 rounded-xl px-4 text-sm font-semibold ${
                  orderHistoryView === "trash"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Trash ({trashedOrders.length})
              </button>
            </div>
          </div>
          {isLoadingOrders ? (
            <div className="mt-4 rounded-2xl bg-gray-50 p-5 text-gray-500">
              Loading order history...
            </div>
          ) : displayedOrders.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-dashed border-gray-300 p-5 text-sm text-gray-500">
              {orderHistoryView === "trash"
                ? "Trash is empty."
                : "No finalized medication orders yet."}
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {displayedOrders.slice(0, 10).map((order) => {
                const receiptSummary = getOrderReceiptSummary(
                  order,
                  resolveInventoryItemForOrderItem,
                );
                const canModifyThisOrder = canModifyOrder(order);
                return (
                  <article
                    key={order.id}
                    className="flex flex-col gap-3 rounded-2xl border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-semibold text-gray-900">
                          {order.patientName || "Medication order"}
                        </div>
                        {receiptSummary.label && (
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${receiptSummary.classes}`}
                          >
                            {receiptSummary.label}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDateTime(order.finalizedAt)} |{" "}
                        {order.transactionType || "normal"} |{" "}
                        {order.items?.length || 0} item
                        {(order.items?.length || 0) === 1 ? "" : "s"}
                      </div>
                      {receiptSummary.orderedTotal > 0 && (
                        <div className="mt-1 text-xs text-gray-500">
                          Received {receiptSummary.receivedTotal} of{" "}
                          {receiptSummary.orderedTotal} ordered units
                        </div>
                      )}
                      {order.isTrashed && (
                        <div className="mt-1 text-xs text-gray-500">
                          Moved to Trash {formatDateTime(order.trashedAt)}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-gray-500">
                        {(order.profileSummaries || [])
                          .map((profile) => profile.patientName)
                          .join(", ")}
                      </div>
                      {order.deliveryAddress && (
                        <div className="mt-1 text-xs text-gray-500">
                          Delivery: {order.deliveryAddress}
                        </div>
                      )}
                      {(order.attachedPrescriptions || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {(order.attachedPrescriptions || []).map(
                            (prescription, index) => (
                              <button
                                key={`${prescription.id || prescription.storagePath || index}`}
                                type="button"
                                onClick={() =>
                                  handleOpenPrescriptionAttachment(prescription)
                                }
                                className="inline-flex min-h-9 max-w-full items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                              >
                                <FaPaperclip className="shrink-0" />
                                <span className="truncate">
                                  {prescription.prescriptionName ||
                                    "Prescription"}
                                  {prescription.patientName
                                    ? ` - ${prescription.patientName}`
                                    : ""}
                                </span>
                              </button>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                      {orderHistoryView === "active" && canModifyThisOrder && (
                        <>
                          {!receiptSummary.isFullyReceived &&
                            receiptSummary.orderedTotal > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  openOrderStockAction(order, "receive")
                                }
                                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                              >
                                Receive stock
                              </button>
                            )}
                          {receiptSummary.hasReceivedStock && (
                            <button
                              type="button"
                              onClick={() =>
                                openOrderStockAction(order, "remove")
                              }
                              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                            >
                              Remove stock
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleEditOrder(order)}
                            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                          >
                            <FaEdit /> Edit
                          </button>
                        </>
                      )}
                      {orderHistoryView === "trash" && canModifyThisOrder && (
                        <button
                          type="button"
                          onClick={() => handleRestoreOrder(order)}
                          className="inline-flex min-h-11 items-center justify-center rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Restore
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleOpenOrderPdf(order)}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <FaFilePdf /> Open PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyOrderTextNotes(order)}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <FaCopy /> Copy notes
                      </button>
                      {orderHistoryView === "active" && canModifyThisOrder && (
                        <button
                          type="button"
                          onClick={() => handleMoveOrderToTrash(order)}
                          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                        >
                          <FaTrash /> Move to Trash
                        </button>
                      )}
                      {orderHistoryView === "trash" && canModifyThisOrder && (
                        <button
                          type="button"
                          onClick={() => handleDeleteOrder(order)}
                          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                        >
                          <FaTrash /> Delete permanently
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}

      {isOrderingView && stockActionOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-3xl bg-white shadow-2xl">
            <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {stockActionMode === "remove"
                    ? "Remove stock from order"
                    : "Receive stock from order"}
                </h3>
                <p className="text-sm text-gray-600">
                  {stockActionMode === "remove"
                    ? "Enter the quantities to remove. Inventory will stop at zero."
                    : "Enter what actually arrived. Leave missing items at zero."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeOrderStockAction}
                className="min-h-11 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <div className="rounded-2xl bg-gray-50 p-4">
                <div className="font-semibold text-gray-900">
                  {stockActionOrder.patientName || "Medication order"}
                </div>
                <div className="text-sm text-gray-600">
                  {formatDateTime(stockActionOrder.finalizedAt)}
                </div>
              </div>

              <div className="mt-4 space-y-5">
                {stockActionGroups.map((transactionGroup) => (
                  <section key={transactionGroup.key} className="space-y-3">
                    <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                      {transactionGroup.title}
                    </div>
                    {transactionGroup.profiles.map((profileGroup) => (
                      <div key={profileGroup.profileId} className="space-y-3">
                        <div className="font-semibold text-gray-900">
                          {profileGroup.patientName}
                        </div>
                        {profileGroup.lines.map((line) => {
                          const isTracked = Boolean(line.inventoryItem);
                          return (
                            <div
                              key={line.key}
                              className="rounded-2xl border border-gray-200 p-4"
                            >
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    {line.item.medicationName}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {[line.item.strength, line.item.form]
                                      .filter(Boolean)
                                      .join(" | ")}
                                  </div>
                                  <div className="mt-1 text-xs text-gray-500">
                                    Ordered {line.orderedQuantity}{" "}
                                    {line.item.unitLabel || "unit"}
                                    {isTracked
                                      ? ` | Current stock ${toNumber(
                                          line.inventoryItem.quantityOnHand,
                                        )}`
                                      : stockActionMode === "receive"
                                        ? " | Will start tracking"
                                        : " | Not tracked"}
                                  </div>
                                  {line.receivedQuantity > 0 && (
                                    <div className="mt-1 text-xs text-gray-500">
                                      Already received {line.receivedQuantity}{" "}
                                      {line.item.unitLabel || "unit"}
                                    </div>
                                  )}
                                </div>
                                <div className="w-full sm:w-40">
                                  <label
                                    htmlFor={`stock-action-${line.key}`}
                                    className="mb-1 block text-xs font-semibold text-gray-600"
                                  >
                                    {stockActionMode === "remove"
                                      ? "Remove quantity"
                                      : "Received quantity"}
                                  </label>
                                  <input
                                    id={`stock-action-${line.key}`}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={stockActionDrafts[line.key] || ""}
                                    onChange={(event) =>
                                      updateStockActionDraft(
                                        line.key,
                                        event.target.value,
                                      )
                                    }
                                    disabled={
                                      stockActionMode === "remove" && !isTracked
                                    }
                                    className="min-h-11 w-full rounded-xl border border-gray-300 px-3 py-2 disabled:bg-gray-100"
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-gray-200 p-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={fillStockActionRemainingQuantities}
                className="min-h-11 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                {stockActionMode === "remove"
                  ? "Fill received quantities"
                  : "Fill remaining quantities"}
              </button>
              <button
                type="button"
                onClick={closeOrderStockAction}
                className="min-h-11 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyOrderStockMovement}
                disabled={!stockActionHasQuantity || isApplyingOrderStock}
                className={`min-h-11 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
                  stockActionMode === "remove"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {isApplyingOrderStock
                  ? "Updating..."
                  : stockActionMode === "remove"
                    ? "Remove from Inventory"
                    : "Add to Inventory"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isOrderingView && previewOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col rounded-3xl bg-white shadow-2xl">
            <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {previewMode === "history"
                    ? "Medication order PDF"
                    : "Preview medication order"}
                </h3>
                <p className="text-sm text-gray-600">
                  {previewMode === "history"
                    ? "Open, review, or share this finalized order PDF."
                    : "Review the PDF before saving it to history or exporting it."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeOrderPreview}
                className="min-h-11 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                {previewMode === "history" ? "Close" : "Back to edit"}
              </button>
            </div>

            <div className="min-h-0 flex-1 bg-gray-100 p-4">
              {previewPdfUrl ? (
                <iframe
                  title="Medication order PDF preview"
                  src={previewPdfUrl}
                  className="h-[60vh] w-full rounded-2xl border border-gray-300 bg-white"
                />
              ) : (
                <div className="flex h-[60vh] items-center justify-center rounded-2xl bg-white text-gray-500">
                  Preparing PDF preview...
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 border-t border-gray-200 p-4 sm:flex-row sm:justify-end">
              {previewMode === "history" ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleForwardOrderPdf(previewOrder)}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <FaShareAlt /> Share PDF
                  </button>
                  <button
                    type="button"
                    onClick={closeOrderPreview}
                    className="min-h-11 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={exportPreviewPdfOnly}
                    className="min-h-11 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Export PDF only
                  </button>
                  <button
                    type="button"
                    onClick={() => savePreviewOrder({ exportPdf: false })}
                    disabled={isFinalizingOrder}
                    className="min-h-11 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
                  >
                    Save record
                  </button>
                  <button
                    type="button"
                    onClick={() => savePreviewOrder({ exportPdf: true })}
                    disabled={isFinalizingOrder}
                    className="min-h-11 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
                  >
                    Save and export PDF
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicationInventoryManager;
